import type {
  ArbeitsbereichEntwurf,
  ArbeitsmittelEntwurf,
  ArbeitsmittelGestelltVon,
  GbuAnlass,
  GbuHinweis,
  GbuKiAuswahl,
  GbuKiKopfVorschlag,
  GbuKiVorschlag,
  GbuKiVorschlagGefaehrdung,
  GbuKopfEntwurf,
  GbuNotfall,
  GbuRollen,
  GbuStatus,
  GbuUnterschrift,
  GbuVoll,
  GefaehrdungEntwurf,
  GefaehrdungsFaktor,
  Kataster,
  MassnahmeArt,
  MassnahmeEntwurf,
  MassnahmeListenZeile,
  MassnahmeVerweis,
  RisikoStufe,
  Signalwort,
  SifaArbeitsbereich,
  SifaArbeitsmittel,
  SifaGbu,
  SifaGbuFassung,
  SifaGbuGefaehrdung,
  SifaGbuMassnahme,
  SifaGbuVorsorge,
  SifaMitarbeiterBereich,
  SifaStoff,
  StoffArt,
  StoffEntwurf,
  UnterschriftRolle,
  VorsorgeArt,
  VorsorgeEntwurf,
} from '../../../services/sifa/gbuTypes';
import type { SifaMitarbeiter } from '../../../services/sifa/types';
import type { GbuZeit } from '../../../services/sifa/gbuService';
import { gbuZeitLogId } from '../../../services/sifa/gbuBooking';

/**
 * Die Regeln der GBU-Oberflaeche (Innendienst) — ohne React, ohne DOM, ohne
 * Supabase; aus den Fachmodulen nur Typen und eine Kennung (siehe unten).
 *
 * WARUM hier: es gibt kein jsdom, Komponenten sind nicht pruefbar. Alles,
 * was in dieser Oberflaeche eine ENTSCHEIDUNG trifft — welcher Knopf ist
 * frei, welche KI-Zeile ist vorangehakt, wann ist die Pruefung ueberfaellig,
 * wie lautet der Freigabe-Link —, steht deshalb hier und in
 * gbuModulLogic.test.ts.
 *
 * Die FACHLICHEN Regeln (Risiko, feste Hinweise, Fassungsinhalt, Buchung)
 * liegen in services/sifa/gbuLogic.ts und gbuBooking.ts. Dieses Modul
 * ergaenzt sie um das, was nur die Bedienung angeht. Es importiert von dort
 * nur Typen und die stabile Log-Kennung (gbuBooking.gbuZeitLogId).
 *
 * Datumswerte sind Zeichenketten `yyyy-mm-dd` und werden als solche
 * verglichen — nie ueber `new Date` geparst (Zeitzonenfehler).
 */

// ─── Allgemein ──────────────────────────────────────────────────────────────

/** Leere oder nur aus Leerzeichen bestehende Eingabe als null, sonst getrimmt. */
export const leerZuNull = (text: string | null | undefined): string | null => {
  const t = (text ?? '').trim();
  return t ? t : null;
};

/**
 * Fasst Leerraum zusammen und trimmt, wirft Leeres weg und entfernt Doppelte
 * (ohne Gross-/Kleinschreibung); die Reihenfolge bleibt. GENAU wie der Dienst
 * (gbuService.textListe) — sonst bliebe „geaendert" nach dem Speichern wahr
 * und der automatische Lauf schriebe immer wieder.
 */
export const bereinigeListe = (werte: ReadonlyArray<string | null | undefined> | null | undefined): string[] => {
  const gesehen = new Set<string>();
  const out: string[] = [];
  for (const w of werte ?? []) {
    const t = (w ?? '').replace(/\s+/g, ' ').trim();
    if (!t) continue;
    const k = t.toLowerCase();
    if (gesehen.has(k)) continue;
    gesehen.add(k);
    out.push(t);
  }
  return out;
};

const ISO_DATUM = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Ist der Wert ein gueltiges ISO-Datum `yyyy-mm-dd` (Monat 1–12, Tag passend zum Monat)? */
export const istIsoDatum = (wert: string | null | undefined): boolean => {
  const m = ISO_DATUM.exec((wert ?? '').trim());
  if (!m) return false;
  const jahr = Number(m[1]);
  const monat = Number(m[2]);
  const tag = Number(m[3]);
  if (monat < 1 || monat > 12 || tag < 1) return false;
  const schalt = (jahr % 4 === 0 && jahr % 100 !== 0) || jahr % 400 === 0;
  const tage = [31, schalt ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][monat - 1];
  return tag <= tage;
};

const BERLIN_TAG = new Intl.DateTimeFormat('de-DE', { timeZone: 'Europe/Berlin', year: 'numeric', month: '2-digit', day: '2-digit' });

/**
 * Datum fuer die Anzeige als TT.MM.JJJJ. Ein reines Datum `yyyy-mm-dd` wird
 * als Zeichenkette umgestellt (nie ueber new Date); ein ZEITSTEMPEL (Freigabe,
 * Unterschrift, Link-Ablauf) zaehlt als Berliner Tag — 22:30 UTC am 20.10. ist
 * in Berlin schon der 21.10. Dieselbe Regel wie gbuPdf.datumDe, damit Editor
 * und Blatt nie einen Tag auseinanderliegen. Leer → „—", Unlesbares bleibt.
 */
export const formatTag = (wert: string | null | undefined): string => {
  const s = (wert ?? '').trim();
  if (!s) return '—';
  const nurDatum = ISO_DATUM.exec(s);
  if (nurDatum) return `${nurDatum[3]}.${nurDatum[2]}.${nurDatum[1]}`;
  if (/^\d{4}-\d{2}-\d{2}T.*(Z|[+-]\d{2}:?\d{2})$/i.test(s)) {
    const ms = Date.parse(s);
    if (Number.isFinite(ms)) {
      const teile = BERLIN_TAG.formatToParts(ms);
      const teil = (typ: string) => teile.find((p) => p.type === typ)?.value ?? '';
      return `${teil('day')}.${teil('month')}.${teil('year')}`;
    }
  }
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : s;
};

/** Deutscher Name des Angemeldeten, sonst die E-Mail, sonst null. */
export const nutzerName = (profil: { first_name?: string | null; last_name?: string | null; email?: string | null } | null | undefined): string | null => {
  if (!profil) return null;
  const name = `${profil.first_name || ''} ${profil.last_name || ''}`.trim();
  return name || (profil.email || '').trim() || null;
};

/** Zahl aus einer deutschen Eingabe („87,5"); null bei leer oder unlesbar. */
export const parseZahl = (text: string | null | undefined): number | null => {
  const t = (text ?? '').trim().replace(/\s+/g, '').replace(',', '.');
  if (!t) return null;
  if (!/^-?\d+(\.\d+)?$/.test(t)) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
};

/** Zahl fuer ein Eingabefeld zurueck in deutsche Schreibweise. */
export const zahlText = (n: number | null | undefined): string =>
  n == null || !Number.isFinite(n) ? '' : String(n).replace('.', ',');

// ─── Status und Pruefdatum ──────────────────────────────────────────────────

export const GBU_STATUS_LABEL: Record<GbuStatus, string> = {
  entwurf: 'Entwurf',
  in_freigabe: 'In Freigabe',
  freigegeben: 'Freigegeben',
  archiviert: 'Archiviert',
};

export const ANLASS_LABEL: Record<GbuAnlass, string> = {
  ersterstellung: 'Ersterstellung',
  aenderung: 'Änderung im Betrieb',
  ereignis: 'Unfall / Ereignis',
  regelpruefung: 'Regelmäßige Überprüfung',
};

export const ANLAESSE: GbuAnlass[] = ['ersterstellung', 'aenderung', 'ereignis', 'regelpruefung'];

/**
 * Der Statustext der Liste und des Editors. Eine Fortschreibung (Entwurf mit
 * bereits freigegebener Vorfassung) nennt beide Fassungen — sonst sieht der
 * Innendienst nicht, dass die alte Fassung noch gilt.
 */
export const statusText = (g: Pick<SifaGbu, 'status' | 'fassung' | 'freigegebeneFassung'>): string => {
  switch (g.status) {
    case 'entwurf':
      return g.freigegebeneFassung != null
        ? `Entwurf Fassung ${g.fassung} · gültig: Fassung ${g.freigegebeneFassung}`
        : 'Entwurf';
    case 'in_freigabe':
      return `In Freigabe · Fassung ${g.fassung}`;
    case 'freigegeben':
      return `Freigegeben · Fassung ${g.freigegebeneFassung ?? g.fassung}`;
    case 'archiviert':
      return 'Archiviert';
    default:
      return String(g.status);
  }
};

/** Farbe des Status-Chips (Tailwind-Klassen). */
export const STATUS_TON: Record<GbuStatus, string> = {
  entwurf: 'text-amber-700 bg-amber-50 border-amber-200',
  in_freigabe: 'text-blue-700 bg-blue-50 border-blue-200',
  freigegeben: 'text-emerald-700 bg-emerald-50 border-emerald-200',
  archiviert: 'text-slate-600 bg-slate-100 border-slate-200',
};

/** Pruefdatum ueberschritten? Nur fuer nicht archivierte GBUs mit Datum; Vergleich als Zeichenkette. */
export const pruefungUeberschritten = (g: Pick<SifaGbu, 'status' | 'pruefenBis'>, heute: string): boolean =>
  g.status !== 'archiviert' && istIsoDatum(g.pruefenBis) && istIsoDatum(heute) && (g.pruefenBis as string) < heute;

// ─── Liste ──────────────────────────────────────────────────────────────────

export type GbuListenFilter = 'aktiv' | 'entwurf' | 'in_freigabe' | 'freigegeben' | 'faellig' | 'archiviert' | 'alle';

export const GBU_FILTER_LABEL: Record<GbuListenFilter, string> = {
  aktiv: 'Alle aktiven',
  entwurf: 'Entwürfe',
  in_freigabe: 'In Freigabe',
  freigegeben: 'Freigegeben',
  faellig: 'Prüfung überschritten',
  archiviert: 'Archiviert',
  alle: 'Alle',
};

export const GBU_FILTER: GbuListenFilter[] = ['aktiv', 'entwurf', 'in_freigabe', 'freigegeben', 'faellig', 'archiviert', 'alle'];

export const filtereGbus = (liste: SifaGbu[], filter: GbuListenFilter, heute: string): SifaGbu[] => {
  const l = liste ?? [];
  switch (filter) {
    case 'aktiv': return l.filter((g) => g.status !== 'archiviert');
    case 'entwurf':
    case 'in_freigabe':
    case 'freigegeben':
    case 'archiviert':
      return l.filter((g) => g.status === filter);
    case 'faellig': return l.filter((g) => pruefungUeberschritten(g, heute));
    default: return l;
  }
};

/** Nach Nummer aufsteigend (GBU 1, 2, 3 …), Archivierte ans Ende. */
export const sortiereGbus = (liste: SifaGbu[]): SifaGbu[] =>
  [...(liste ?? [])].sort((a, b) => {
    const aa = a.status === 'archiviert' ? 1 : 0;
    const ba = b.status === 'archiviert' ? 1 : 0;
    return aa - ba || (a.nummer || 0) - (b.nummer || 0);
  });

export interface GbuZaehler {
  aktiv: number;
  entwurf: number;
  inFreigabe: number;
  freigegeben: number;
  faellig: number;
  archiviert: number;
}

export const zaehleGbus = (liste: SifaGbu[], heute: string): GbuZaehler => {
  const z: GbuZaehler = { aktiv: 0, entwurf: 0, inFreigabe: 0, freigegeben: 0, faellig: 0, archiviert: 0 };
  for (const g of liste ?? []) {
    if (g.status === 'archiviert') { z.archiviert += 1; continue; }
    z.aktiv += 1;
    if (g.status === 'entwurf') z.entwurf += 1;
    else if (g.status === 'in_freigabe') z.inFreigabe += 1;
    else if (g.status === 'freigegeben') z.freigegeben += 1;
    if (pruefungUeberschritten(g, heute)) z.faellig += 1;
  }
  return z;
};

/** „Rohbau · Einschalen" bzw. nur die Bezeichnung; unbekannte Id = „—". */
export const bereichName = (bereiche: ReadonlyArray<SifaArbeitsbereich>, id: string | null | undefined): string => {
  const b = (bereiche ?? []).find((x) => x.id === id);
  if (!b) return '—';
  return b.bereich ? `${b.bereich} · ${b.bezeichnung}` : b.bezeichnung;
};

// ─── Anlegen ────────────────────────────────────────────────────────────────

/** Aktive Arbeitsbereiche fuer die Auswahl „Neue GBU", in Pflegereihenfolge. */
export const aktiveBereiche = (bereiche: ReadonlyArray<SifaArbeitsbereich>): SifaArbeitsbereich[] =>
  [...(bereiche ?? [])]
    .filter((b) => b.aktiv)
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.bezeichnung.localeCompare(b.bezeichnung, 'de'));

export const leererNotfall = (): GbuNotfall => ({
  ersteHilfe: '',
  brand: '',
  notruf: '',
  zugaenge: '',
  meldung: '',
  kontakte: '',
});

export const NOTFALL_FELDER: Array<{ feld: keyof GbuNotfall; label: string; platzhalter: string }> = [
  { feld: 'ersteHilfe', label: 'Erste Hilfe', platzhalter: 'Ersthelfer, Verbandkasten, Augenspülflasche …' },
  { feld: 'brand', label: 'Brandschutz', platzhalter: 'Feuerlöscher, Fluchtwege, Sammelplatz …' },
  { feld: 'notruf', label: 'Notruf', platzhalter: '112 – Wo? Was? Wie viele? Welche Verletzungen? Warten auf Rückfragen.' },
  { feld: 'zugaenge', label: 'Zugänge / Rettungswege', platzhalter: 'Zufahrt für Rettungsdienst, Einweiser …' },
  { feld: 'meldung', label: 'Meldung von Unfällen', platzhalter: 'Meldung an Vorgesetzten, Verbandbuch, Unfallanzeige ab 3 Tagen AU …' },
  { feld: 'kontakte', label: 'Kontakte', platzhalter: 'Durchgangsarzt, Giftnotruf, Betriebsarzt …' },
];

/**
 * Vorgabe der Rollen (Plan §8): Erstellt = angemeldeter Nutzer,
 * Freigegeben = Ansprechpartner aus dem Projekt, Geprueft = leer (optional).
 */
export const vorgabeRollen = (eingabe: { nutzerName: string | null; ansprechpartner: string | null }): GbuRollen => ({
  erstellt: { name: (eingabe.nutzerName || '').trim(), funktion: 'Fachkraft für Arbeitssicherheit' },
  geprueft: null,
  freigegeben: { name: (eingabe.ansprechpartner || '').trim(), funktion: 'Unternehmer' },
});

/** Der vollstaendige Kopf einer neuen GBU — leer bis auf Bereich, Titel, Anlass und Rollen. */
export const neuerGbuKopf = (eingabe: { bereichId: string; titel: string; anlass: GbuAnlass; rollen: GbuRollen }): GbuKopfEntwurf => ({
  bereichId: eingabe.bereichId,
  titel: eingabe.titel.trim(),
  anlass: eingabe.anlass,
  beteiligte: null,
  unterlagen: null,
  vorschriften: [],
  psaGrundausstattung: null,
  randbedingungen: null,
  umwelt: null,
  koordination: null,
  orgMassnahmen: [],
  notfall: leererNotfall(),
  rollen: eingabe.rollen,
});

/** Was fuer „Neue GBU" noch fehlt; leer = anlegen erlaubt. */
export const pruefeNeueGbu = (eingabe: { bereichId: string | null | undefined; titel: string | null | undefined }): string[] => {
  const fehler: string[] = [];
  if (!(eingabe.bereichId || '').trim()) fehler.push('Bitte einen Arbeitsbereich wählen.');
  if (!(eingabe.titel || '').trim()) fehler.push('Bitte einen Titel eingeben.');
  return fehler;
};

// ─── Kopf (Abschnitte) ──────────────────────────────────────────────────────

/** Der Kopf einer GBU als bearbeitbarer Entwurf (tiefe Kopie). */
export const kopfAus = (g: SifaGbu): GbuKopfEntwurf => ({
  bereichId: g.bereichId,
  titel: g.titel || '',
  anlass: g.anlass,
  beteiligte: g.beteiligte ?? null,
  unterlagen: g.unterlagen ?? null,
  vorschriften: [...(g.vorschriften ?? [])],
  psaGrundausstattung: g.psaGrundausstattung ?? null,
  randbedingungen: g.randbedingungen ?? null,
  umwelt: g.umwelt ?? null,
  koordination: g.koordination ?? null,
  orgMassnahmen: [...(g.orgMassnahmen ?? [])],
  notfall: { ...leererNotfall(), ...(g.notfall ?? {}) },
  rollen: {
    erstellt: { ...(g.rollen?.erstellt ?? { name: '', funktion: '' }) },
    geprueft: g.rollen?.geprueft ? { ...g.rollen.geprueft } : null,
    freigegeben: { ...(g.rollen?.freigegeben ?? { name: '', funktion: '' }) },
  },
});

export type KopfAbschnitt = 'kopf' | 'org' | 'notfall' | 'rollen';

/** Welche Felder ein Abschnitt speichert — „Speichern je Abschnitt". */
export const KOPF_ABSCHNITTE: Record<KopfAbschnitt, ReadonlyArray<keyof GbuKopfEntwurf>> = {
  kopf: ['titel', 'bereichId', 'anlass', 'beteiligte', 'unterlagen', 'vorschriften', 'psaGrundausstattung', 'randbedingungen', 'umwelt', 'koordination'],
  org: ['orgMassnahmen'],
  notfall: ['notfall'],
  rollen: ['rollen'],
};

export const KOPF_ABSCHNITT_LISTE: KopfAbschnitt[] = ['kopf', 'org', 'notfall', 'rollen'];

/** Bereinigt die Felder eines Kopf-Entwurfs fuer das Speichern (Texte getrimmt, Listen ohne Leeres/Doppeltes). */
export const bereinigeKopf = (k: GbuKopfEntwurf): GbuKopfEntwurf => {
  const rolle = (r: { name: string; funktion: string }) => ({ name: (r?.name || '').trim(), funktion: (r?.funktion || '').trim() });
  const notfall = { ...leererNotfall(), ...(k.notfall ?? {}) };
  return {
    bereichId: k.bereichId,
    titel: (k.titel || '').trim(),
    anlass: k.anlass,
    beteiligte: leerZuNull(k.beteiligte),
    unterlagen: leerZuNull(k.unterlagen),
    vorschriften: bereinigeListe(k.vorschriften),
    psaGrundausstattung: leerZuNull(k.psaGrundausstattung),
    randbedingungen: leerZuNull(k.randbedingungen),
    umwelt: leerZuNull(k.umwelt),
    koordination: leerZuNull(k.koordination),
    orgMassnahmen: bereinigeListe(k.orgMassnahmen),
    notfall: {
      ersteHilfe: (notfall.ersteHilfe || '').trim(),
      brand: (notfall.brand || '').trim(),
      notruf: (notfall.notruf || '').trim(),
      zugaenge: (notfall.zugaenge || '').trim(),
      meldung: (notfall.meldung || '').trim(),
      kontakte: (notfall.kontakte || '').trim(),
    },
    rollen: {
      erstellt: rolle(k.rollen.erstellt),
      geprueft: k.rollen.geprueft ? rolle(k.rollen.geprueft) : null,
      freigegeben: rolle(k.rollen.freigegeben),
    },
  };
};

const gleich = (a: unknown, b: unknown): boolean => JSON.stringify(a) === JSON.stringify(b);

/**
 * Nur die GEAENDERTEN Felder eines Abschnitts — das, was speichereGbuKopf
 * bekommt. Verglichen wird bereinigt gegen bereinigt: ein angehaengtes
 * Leerzeichen ist keine Aenderung.
 */
export const kopfAenderung = (gbu: SifaGbu, entwurf: GbuKopfEntwurf, abschnitt: KopfAbschnitt): Partial<GbuKopfEntwurf> => {
  const vorher = bereinigeKopf(kopfAus(gbu));
  const nachher = bereinigeKopf(entwurf);
  const out: Partial<GbuKopfEntwurf> = {};
  for (const feld of KOPF_ABSCHNITTE[abschnitt]) {
    if (!gleich(vorher[feld], nachher[feld])) (out as Record<string, unknown>)[feld] = nachher[feld];
  }
  return out;
};

export const abschnittGeaendert = (gbu: SifaGbu, entwurf: GbuKopfEntwurf, abschnitt: KopfAbschnitt): boolean =>
  Object.keys(kopfAenderung(gbu, entwurf, abschnitt)).length > 0;

/**
 * Neuer Stand vom Server (Speichern eines Abschnitts, KI-Uebernahme,
 * Neuladen) ohne die ungespeicherten Eingaben der ANDEREN Abschnitte zu
 * verlieren: ein Abschnitt, der gegenueber dem alten Stand nicht geaendert
 * war, uebernimmt den neuen; ein geaenderter behaelt die Eingabe. Sagt der
 * neue Stand nach Bereinigung dasselbe wie der Entwurf, bleibt der Entwurf
 * wie getippt (kein Springen im Feld, kein verschlucktes Leerzeichen).
 *
 * NUR fuer FREMDE Staende — die eigene Antwort eines Speicherlaufs setzt nur
 * die Basis (kopfFolge 'nurBasis'), sonst ginge waehrend des Laufs Getipptes
 * verloren.
 */
export const rebaseKopf = (alteBasis: SifaGbu, neueBasis: SifaGbu, entwurf: GbuKopfEntwurf): GbuKopfEntwurf => {
  const neu = kopfAus(neueBasis);
  const out: GbuKopfEntwurf = { ...neu };
  for (const abschnitt of Object.keys(KOPF_ABSCHNITTE) as KopfAbschnitt[]) {
    if (!abschnittGeaendert(alteBasis, entwurf, abschnitt) && abschnittGeaendert(neueBasis, entwurf, abschnitt)) continue;
    for (const feld of KOPF_ABSCHNITTE[abschnitt]) (out as Record<string, unknown>)[feld] = entwurf[feld];
  }
  return out;
};

/** Was ein Abschnitt vor dem Speichern noch braucht; leer = speichern erlaubt. */
export const pruefeKopfAbschnitt = (entwurf: GbuKopfEntwurf, abschnitt: KopfAbschnitt): string[] => {
  const fehler: string[] = [];
  if (abschnitt === 'kopf') {
    if (!(entwurf.titel || '').trim()) fehler.push('Der Titel darf nicht leer sein.');
    if (!(entwurf.bereichId || '').trim()) fehler.push('Bitte einen Arbeitsbereich wählen.');
  }
  if (abschnitt === 'rollen') {
    if (!(entwurf.rollen?.erstellt?.name || '').trim()) fehler.push('„Erstellt" braucht einen Namen.');
    if (!(entwurf.rollen?.freigegeben?.name || '').trim()) fehler.push('„Freigegeben" braucht einen Namen.');
    if (entwurf.rollen?.geprueft && !(entwurf.rollen.geprueft.name || '').trim()) fehler.push('„Geprüft" ist eingeschaltet, aber ohne Namen.');
  }
  return fehler;
};

/** Setzt einen Wert in eine Liste an Stelle i; fuer Listen-Editoren (Vorschriften, Abschnitt 5). */
export const ersetzeAn = <T,>(liste: ReadonlyArray<T>, index: number, wert: T): T[] =>
  liste.map((e, i) => (i === index ? wert : e));

export const entferneAn = <T,>(liste: ReadonlyArray<T>, index: number): T[] => liste.filter((_, i) => i !== index);

/** Haengt einen Eintrag an, wenn er nicht leer und noch nicht enthalten ist. */
export const fuegeHinzu = (liste: ReadonlyArray<string>, wert: string): string[] => bereinigeListe([...liste, wert]);

// ─── Automatisch speichern ──────────────────────────────────────────────────

/** Entprellung: gespeichert wird so lange nach der LETZTEN Eingabe (sofort beim Verlassen des Feldes). */
export const AUTO_SPEICHER_MS = 800;

/** Was der Speicherlauf einer Einheit (Kopf, Gefaehrdung, Massnahme, Vorsorge) zuletzt getan hat. */
export type SpeicherPhase = 'ruhig' | 'speichert' | 'gespeichert' | 'fehler';

/** Was der Nutzer klein und ruhig neben dem Bereich sieht. */
export type SpeicherAnzeige = 'ruhig' | 'wartet' | 'speichert' | 'gespeichert' | 'fehler' | 'unvollstaendig';

/**
 * Anzeige aus Phase und Entwurf:
 *   speichert       — ein Lauf ist unterwegs,
 *   fehler          — der letzte Lauf scheiterte, die Eingabe ist noch nicht gespeichert,
 *   wartet          — geaendert, gueltig, die Entprellung laeuft,
 *   unvollstaendig  — geaendert, aber (noch) nicht speicherbar (z. B. Text leer),
 *   gespeichert     — nichts offen, der letzte Lauf gelang,
 *   ruhig           — nichts offen, nichts zu melden.
 */
export const speicherAnzeige = (phase: SpeicherPhase, geaendert: boolean, bereit: boolean): SpeicherAnzeige => {
  if (phase === 'speichert') return 'speichert';
  if (geaendert) {
    if (phase === 'fehler') return 'fehler';
    return bereit ? 'wartet' : 'unvollstaendig';
  }
  return phase === 'gespeichert' ? 'gespeichert' : 'ruhig';
};

export const SPEICHER_TEXT: Record<SpeicherAnzeige, string> = {
  ruhig: '',
  wartet: 'Speichert…',
  speichert: 'Speichert…',
  gespeichert: 'Gespeichert',
  fehler: 'Nicht gespeichert — erneut versuchen',
  unvollstaendig: 'Nicht gespeichert',
};

/**
 * Was der Kopf beim naechsten Lauf speichert: die Felder ALLER geaenderten
 * und gueltigen Abschnitte in EINEM Aufruf (je GBU nur ein Kopf-Speichern
 * gleichzeitig — die optimistische Sperre haengt an der Revision des ganzen
 * Kopfs). Geaenderte, aber ungueltige Abschnitte (z. B. Titel leer) bleiben
 * mit ihren Gruenden stehen und werden nicht mitgeschickt.
 */
export const kopfSpeicherPlan = (gbu: SifaGbu, entwurf: GbuKopfEntwurf): {
  aenderung: Partial<GbuKopfEntwurf>;
  abschnitte: KopfAbschnitt[];
  ungueltig: Array<{ abschnitt: KopfAbschnitt; fehler: string[] }>;
} => {
  const aenderung: Partial<GbuKopfEntwurf> = {};
  const abschnitte: KopfAbschnitt[] = [];
  const ungueltig: Array<{ abschnitt: KopfAbschnitt; fehler: string[] }> = [];
  for (const a of KOPF_ABSCHNITT_LISTE) {
    const teil = kopfAenderung(gbu, entwurf, a);
    if (Object.keys(teil).length === 0) continue;
    const fehler = pruefeKopfAbschnitt(entwurf, a);
    if (fehler.length) { ungueltig.push({ abschnitt: a, fehler }); continue; }
    Object.assign(aenderung, teil);
    abschnitte.push(a);
  }
  return { aenderung, abschnitte, ungueltig };
};

/**
 * Anzeige eines Kopf-Abschnitts aus dem gemeinsamen Kopf-Lauf: ungueltig
 * geht vor; ein geaenderter Abschnitt zeigt den Lauf (wartet/speichert/
 * fehler); „Gespeichert" nur, wenn er im letzten gelungenen Lauf dabei war.
 */
export const abschnittAnzeige = (e: { kopf: SpeicherAnzeige; geaendert: boolean; ungueltig: boolean; warDabei: boolean }): SpeicherAnzeige => {
  if (e.ungueltig) return 'unvollstaendig';
  if (e.geaendert) return e.kopf === 'speichert' || e.kopf === 'fehler' ? e.kopf : 'wartet';
  if (e.kopf === 'speichert' && e.warDabei) return 'speichert';
  return e.warDabei && e.kopf === 'gespeichert' ? 'gespeichert' : 'ruhig';
};

/** Eine neue Gefaehrdung wird erst angelegt, wenn sie etwas sagt (Text nicht leer). */
export const gefaehrdungSinnvoll = (e: Pick<GefaehrdungEntwurf, 'gefaehrdung'>): boolean => (e.gefaehrdung || '').trim() !== '';

/** Eine neue Massnahme wird erst angelegt, wenn sie einen Text hat. */
export const massnahmeSinnvoll = (e: Pick<MassnahmeEntwurf, 'text'>): boolean => (e.text || '').trim() !== '';

/**
 * Gespeicherte und neue (noch nicht angelegte) Zeilen in EINER Liste mit
 * stabilen Schluesseln: eine neue Zeile behaelt nach dem Anlegen ihren
 * Client-Schluessel (`schluesselFuerId`). So bleibt dieselbe Komponente
 * stehen — wer gerade tippt, verliert weder Fokus noch Eingabe.
 */
export const mitStabilenSchluesseln = <T extends { id: string }, N extends { key: string }>(
  gespeichert: ReadonlyArray<T>,
  neu: ReadonlyArray<N>,
  schluesselFuerId: Readonly<Record<string, string>>,
): Array<{ key: string; zeile: T; neu: null } | { key: string; zeile: null; neu: N }> => [
  ...gespeichert.map((z) => ({ key: schluesselFuerId[z.id] ?? z.id, zeile: z, neu: null as null })),
  ...neu.map((n) => ({ key: n.key, zeile: null as null, neu: n })),
];

/** Ist Zeitstempel `a` echt aelter als `b`? Unlesbares zaehlt nie als aelter. */
export const istAelter = (a: string | null | undefined, b: string | null | undefined): boolean => {
  const x = Date.parse(a ?? '');
  const y = Date.parse(b ?? '');
  return Number.isFinite(x) && Number.isFinite(y) && x < y;
};

const gleicheZeit = (a: string | null | undefined, b: string | null | undefined): boolean => {
  const x = Date.parse(a ?? '');
  const y = Date.parse(b ?? '');
  return Number.isFinite(x) && Number.isFinite(y) && x === y;
};

type MitStand = { id: string; updatedAt?: string | null };

/**
 * Der NEUESTE bekannte Stand einer Zeile aus Basis (zuletzt angenommener
 * Prop) und eigener Antwort — nach `updatedAt`; bei Gleichstand gewinnt die
 * eigene Antwort. Gegen ihn wird verglichen und gespeichert, nie gegen einen
 * spaeter eintreffenden aelteren Prop.
 */
export const neuesterStand = <Z extends MitStand>(basis: Z | null, eigen: Z | null): Z | null => {
  if (!basis) return eigen;
  if (!eigen || eigen.id !== basis.id) return basis;
  return istAelter(eigen.updatedAt, basis.updatedAt) ? basis : eigen;
};

/**
 * Ist `neu` gegenueber dem neuesten bekannten Stand veraltet — aelter, oder
 * gleich alt wie die eigene Antwort (dann gilt die eigene)? Ein veralteter
 * Stand (spaete Antwort eines Neuladens) wird DURCHGAENGIG ignoriert: weder
 * Entwurf noch Basis noch Speichervergleich.
 */
export const istVeraltet = <Z extends MitStand>(neu: Z, bekannt: Z | null, eigen: Z | null): boolean => {
  if (!bekannt || bekannt.id !== neu.id || neu === bekannt) return false;
  if (istAelter(neu.updatedAt, bekannt.updatedAt)) return true;
  return bekannt === eigen && gleicheZeit(neu.updatedAt, bekannt.updatedAt);
};

/**
 * Was eine Zeile (Gefaehrdung, Massnahme, Vorsorge) mit einem NEUEN Stand von
 * aussen tut (Prop-Wechsel):
 *   nichts       — kein neuer Stand,
 *   ignorieren   — veraltet gegenueber dem neuesten bekannten Stand (istVeraltet):
 *                  Entwurf, Basis und Vergleich bleiben,
 *   nurBasis     — die EIGENE Antwort (der Lauf hat den Entwurf schon
 *                  behandelt) oder ein neuerer fremder Stand bei Eingaben im
 *                  Entwurf — der Entwurf bleibt, verglichen wird gegen den
 *                  neuen Stand (weicht er ab, speichert der Zeitgeber),
 *   uebernehmen  — neuerer fremder Stand, der Entwurf war gegenueber dem
 *                  neuesten bekannten Stand unveraendert.
 * Lost Update: die eigene Antwort darf NIE wie ein fremder Stand eingespielt
 * werden — waehrend des Laufs Getipptes ginge sonst verloren.
 */
export type ZeilenFolge = 'nichts' | 'ignorieren' | 'nurBasis' | 'uebernehmen';

export const zeilenFolge = <Z extends MitStand>(o: {
  neu: Z | null;
  basis: Z | null;
  /** Zuletzt SELBST gespeicherte Zeile (Antwort des eigenen Laufs). */
  eigen: Z | null;
  /** Weicht der Entwurf vom gegebenen (neuesten bekannten) Stand ab? */
  entwurfGeaendert: (gegen: Z) => boolean;
}): ZeilenFolge => {
  if (!o.neu || o.neu === o.basis) return 'nichts';
  if (o.neu === o.eigen) return 'nurBasis';
  const bekannt = neuesterStand(o.basis, o.eigen);
  if (istVeraltet(o.neu, bekannt, o.eigen)) return 'ignorieren';
  if (!bekannt) return 'nurBasis';
  return o.entwurfGeaendert(bekannt) ? 'nurBasis' : 'uebernehmen';
};

/**
 * Nach der EIGENEN Antwort: hat sich der Entwurf seit Laufbeginn nicht
 * geaendert (dasselbe Objekt) und weicht die Antwort trotz gleicher
 * Bereinigung noch ab (der Dienst hat etwas anders gespeichert), gilt die
 * Antwort — sonst schriebe der Lauf endlos. Gleich nach Bereinigung bleibt
 * der Entwurf wie getippt (kein Springen, kein verschlucktes Leerzeichen).
 */
export const antwortStattEntwurf = <E>(aktuell: E, gesendet: E, antwortWeichtAb: boolean): boolean =>
  aktuell === gesendet && antwortWeichtAb;

/**
 * Was der Kopf mit einem neuen `voll.gbu` tut:
 *   neu       — erste bzw. andere GBU: Entwurf aus dem Stand,
 *   nurBasis  — die EIGENE Antwort (der Lauf hat den Entwurf schon behandelt),
 *   aelter    — kleinere Revision als die bekannte: nicht uebernehmen,
 *   rebase    — fremder neuerer Stand: unveraenderte Abschnitte folgen ihm.
 */
export type KopfFolge = 'neu' | 'nurBasis' | 'aelter' | 'rebase';

export const kopfFolge = (o: { neu: SifaGbu; basis: SifaGbu | null; eigen: SifaGbu | null }): KopfFolge => {
  if (!o.basis || o.basis.id !== o.neu.id) return 'neu';
  if (o.neu === o.eigen) return 'nurBasis';
  if (o.neu.revision < o.basis.revision) return 'aelter';
  return 'rebase';
};

/**
 * Nach dem EIGENEN Kopf-Speichern bei seit Laufbeginn unveraendertem Entwurf:
 * gespeicherte Abschnitte, die nach Bereinigung noch von der Antwort
 * abweichen, uebernehmen die Antwort; alles andere bleibt (auch ungueltige,
 * nicht mitgeschickte Abschnitte). Nichts zu tun → derselbe Entwurf.
 */
export const kopfNachSpeichern = (neu: SifaGbu, entwurf: GbuKopfEntwurf, gespeichert: ReadonlyArray<KopfAbschnitt>): GbuKopfEntwurf => {
  const abweichend = gespeichert.filter((a) => abschnittGeaendert(neu, entwurf, a));
  if (abweichend.length === 0) return entwurf;
  const server = kopfAus(neu);
  const out: GbuKopfEntwurf = { ...entwurf };
  for (const a of abweichend) for (const feld of KOPF_ABSCHNITTE[a]) (out as Record<string, unknown>)[feld] = server[feld];
  return out;
};

/**
 * Zuklappen einer Karte: ist nach dem Speichern noch etwas offen (Fehler,
 * unvollstaendig), bleibt sie beim ersten Klick offen und sagt warum; erst ein
 * zweiter Klick klappt trotzdem zu (der Kartenkopf zeigt den Stand weiter).
 */
export const zuklappenErlaubt = (allesGespeichert: boolean, hinweisSchonGezeigt: boolean): boolean =>
  allesGespeichert || hinweisSchonGezeigt;

export const KLAPP_HINWEIS_TEXT = 'Nicht alles gespeichert — bitte die markierten Stellen prüfen. Nochmals klicken klappt trotzdem zu.';

/** Speichern antwortet nicht (Zeitlimit von alleSpeichern). */
export const SPEICHER_HAENGT_TEXT = 'Das Speichern antwortet seit über 10 Sekunden nicht — bitte die Verbindung prüfen und erneut versuchen. Eingefroren bzw. ausgegeben wird nur der gespeicherte Stand.';

/** Kartenkopf: wie viele Zeilen (Massnahmen, Vorsorge) bzw. Listenfelder der Karte noch nicht gespeichert sind. */
export const offeneZeilenText = (anzahl: number): string =>
  anzahl <= 0 ? '' : anzahl === 1 ? '1 Eingabe darin nicht gespeichert' : `${anzahl} Eingaben darin nicht gespeichert`;

// ─── Risiko (Anzeige) ───────────────────────────────────────────────────────

export const W_STUFEN: Array<{ wert: 1 | 2 | 3; label: string }> = [
  { wert: 1, label: '1 – unwahrscheinlich' },
  { wert: 2, label: '2 – möglich' },
  { wert: 3, label: '3 – wahrscheinlich' },
];

export const S_STUFEN: Array<{ wert: 1 | 2 | 3; label: string }> = [
  { wert: 1, label: '1 – leicht (Erste Hilfe)' },
  { wert: 2, label: '2 – mittel (Arbeitsunfähigkeit)' },
  { wert: 3, label: '3 – schwer (bleibender Schaden, Tod)' },
];

/** W bzw. S auf 1–3 geklemmt; Unsinn wird 1. */
export const klemmeStufe = (n: unknown): 1 | 2 | 3 => {
  const z = Math.round(Number(n));
  if (!Number.isFinite(z) || z <= 1) return 1;
  return z >= 3 ? 3 : 2;
};

/**
 * Stufe zu R = W × S fuer die Anzeige (3×3, Plan §2): 1–2 gering, 3–4 mittel,
 * 6–9 hoch. Die fachliche Fassung steht in gbuLogic.risikoStufe; diese hier
 * dient nur der Farbe und deckt sich mit ihr.
 */
export const anzeigeStufe = (r: number): RisikoStufe => (r >= 6 ? 'hoch' : r >= 3 ? 'mittel' : 'gering');

export const RISIKO_TON: Record<RisikoStufe, { klasse: string; label: string; zelle: string }> = {
  gering: { klasse: 'text-emerald-800 bg-emerald-100 border-emerald-300', label: 'gering', zelle: 'bg-emerald-200 text-emerald-900' },
  mittel: { klasse: 'text-amber-800 bg-amber-100 border-amber-300', label: 'mittel', zelle: 'bg-amber-200 text-amber-900' },
  hoch: { klasse: 'text-red-800 bg-red-100 border-red-300', label: 'hoch', zelle: 'bg-red-300 text-red-950' },
};

/**
 * Die 3×3-Matrix der Legende: Zeilen S 3→1, Spalten W 1→3. `stufe` ist
 * austauschbar, damit die Oberflaeche die fachliche Fassung
 * (gbuLogic.risikoStufe) einsetzt.
 */
export const matrixZeilen = (stufe: (r: number) => RisikoStufe = anzeigeStufe): Array<{ s: number; zellen: Array<{ w: number; r: number; stufe: RisikoStufe }> }> =>
  [3, 2, 1].map((s) => ({ s, zellen: [1, 2, 3].map((w) => ({ w, r: w * s, stufe: stufe(w * s) })) }));

// ─── Gefaehrdung ────────────────────────────────────────────────────────────

/**
 * Beschriftung des Loeschknopfs einer Gefaehrdung, Massnahme oder Vorsorge.
 * Vor der ersten Freigabe wird geloescht; danach markiert der Dienst die
 * Zeile nur „entfaellt ab Fassung n" — die gueltige Fassung und ihre
 * Umsetzungs- und Wirksamkeitsnachweise bleiben erhalten.
 */
export const loeschText = (g: Pick<SifaGbu, 'freigegebeneFassung' | 'fassung'>): { label: string; erklaerung: string | null } =>
  g.freigegebeneFassung == null
    ? { label: 'Löschen', erklaerung: null }
    : {
      label: `Entfällt ab Fassung ${g.fassung}`,
      erklaerung: `Nach einer Freigabe wird nicht gelöscht: die Zeile entfällt ab Fassung ${g.fassung}. Fassung ${g.freigegebeneFassung} und ihre Nachweise bleiben, wiederherstellen geht bis zur Freigabe.`,
    };

/** „Entfällt ab Fassung 3" bzw. leer fuer eine aktive Zeile. */
export const entfaelltText = (z: { entfaelltAbFassung: number | null }): string =>
  z.entfaelltAbFassung == null ? '' : `Entfällt ab Fassung ${z.entfaelltAbFassung}`;

/** DOM-Id der Karte einer Gefaehrdung — Sprungziel aus den Pruefhinweisen. */
export const gefaehrdungAnker = (id: string): string => `gbu-gefaehrdung-${id}`;

/** Naechster freier sortOrder (max + 1, ab 1). */
export const naechsteSortOrder = (liste: ReadonlyArray<{ sortOrder: number }>): number =>
  (liste ?? []).reduce((max, e) => Math.max(max, Number(e?.sortOrder) || 0), 0) + 1;

export const sortiereNachSortOrder = <T extends { sortOrder: number; createdAt?: string }>(liste: ReadonlyArray<T>): T[] =>
  [...(liste ?? [])].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || (a.createdAt || '').localeCompare(b.createdAt || ''));

export const neueGefaehrdung = (sortOrder: number, faktor: GefaehrdungsFaktor = 'mechanisch'): GefaehrdungEntwurf => ({
  faktor,
  gefaehrdung: '',
  folgen: null,
  wVor: 2,
  sVor: 2,
  wNach: 1,
  sNach: 2,
  arbeitsmittelIds: [],
  stoffIds: [],
  vorschriften: [],
  quelle: 'manuell',
  sortOrder,
});

export const gefaehrdungEntwurfAus = (g: SifaGbuGefaehrdung): GefaehrdungEntwurf => ({
  faktor: g.faktor,
  gefaehrdung: g.gefaehrdung || '',
  folgen: g.folgen ?? null,
  wVor: klemmeStufe(g.wVor),
  sVor: klemmeStufe(g.sVor),
  wNach: klemmeStufe(g.wNach),
  sNach: klemmeStufe(g.sNach),
  arbeitsmittelIds: [...(g.arbeitsmittelIds ?? [])],
  stoffIds: [...(g.stoffIds ?? [])],
  vorschriften: [...(g.vorschriften ?? [])],
  quelle: g.quelle,
  sortOrder: g.sortOrder,
});

export const bereinigeGefaehrdung = (e: GefaehrdungEntwurf): GefaehrdungEntwurf => ({
  ...e,
  gefaehrdung: (e.gefaehrdung || '').trim(),
  folgen: leerZuNull(e.folgen),
  wVor: klemmeStufe(e.wVor),
  sVor: klemmeStufe(e.sVor),
  wNach: klemmeStufe(e.wNach),
  sNach: klemmeStufe(e.sNach),
  // Wie der Dienst (textListe) — auch die Bezugs-Ids.
  arbeitsmittelIds: bereinigeListe(e.arbeitsmittelIds),
  stoffIds: bereinigeListe(e.stoffIds),
  vorschriften: bereinigeListe(e.vorschriften),
});

export const gefaehrdungGeaendert = (g: SifaGbuGefaehrdung, e: GefaehrdungEntwurf): boolean =>
  !gleich(bereinigeGefaehrdung(gefaehrdungEntwurfAus(g)), bereinigeGefaehrdung(e));

/**
 * Eine NEUE Gefaehrdung hat Eingaben (Faktor, W/S, Bezug, Text …) gegenueber
 * ihrer Vorlage — auch ohne Text. Dann gilt sie als ungespeichert
 * („unvollstaendig", Rueckfrage beim Zurueck); angelegt wird sie erst mit Text.
 */
export const gefaehrdungBegonnen = (vorlage: GefaehrdungEntwurf, e: GefaehrdungEntwurf): boolean =>
  !gleich(bereinigeGefaehrdung(vorlage), bereinigeGefaehrdung(e));

export const pruefeGefaehrdung = (e: GefaehrdungEntwurf): string[] =>
  (e.gefaehrdung || '').trim() ? [] : ['Bitte die Gefährdung beschreiben.'];

/** Haken an/aus in einer Id-Liste (Bezug Arbeitsmittel/Stoffe, Mitarbeiter). */
export const schalteId = (ids: ReadonlyArray<string>, id: string): string[] =>
  ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id];

/**
 * Was im Bezug-Feld zur Wahl steht: aktive Eintraege und alle, die schon
 * angehakt sind (auch inaktive — sonst verschwaende ein Bezug still).
 */
export const bezugsAuswahl = <T extends { id: string; aktiv: boolean; bezeichnung: string }>(liste: ReadonlyArray<T>, gewaehlt: ReadonlyArray<string>): T[] =>
  [...(liste ?? [])]
    .filter((x) => x.aktiv || gewaehlt.includes(x.id))
    .sort((a, b) => a.bezeichnung.localeCompare(b.bezeichnung, 'de'));

// ─── Massnahme ──────────────────────────────────────────────────────────────

export const MASSNAHME_ARTEN: Array<{ art: MassnahmeArt; label: string; kurz: string }> = [
  { art: 'S', label: 'Substitution', kurz: 'S' },
  { art: 'T', label: 'Technisch', kurz: 'T' },
  { art: 'O', label: 'Organisatorisch', kurz: 'O' },
  { art: 'P', label: 'Persönlich (PSA)', kurz: 'P' },
];

export const MASSNAHME_ART_TON: Record<MassnahmeArt, string> = {
  S: 'text-violet-800 bg-violet-100',
  T: 'text-sky-800 bg-sky-100',
  O: 'text-amber-800 bg-amber-100',
  P: 'text-rose-800 bg-rose-100',
};

export const VERWEIS_LABEL: Record<MassnahmeVerweis, string> = {
  ba: 'Betriebsanweisung',
  unterweisung: 'Unterweisung',
  pruefung: 'Prüfung',
  vorsorge: 'Vorsorge',
};

export const VERWEISE: MassnahmeVerweis[] = ['ba', 'unterweisung', 'pruefung', 'vorsorge'];

export const neueMassnahme = (sortOrder: number, art: MassnahmeArt = 'T'): MassnahmeEntwurf => ({
  art,
  text: '',
  psaCode: null,
  verweis: null,
  verantwortlich: null,
  termin: null,
  umgesetztGemeldetAm: null,
  umgesetztGemeldetVon: null,
  umgesetztKommentar: null,
  wirksamGeprueftAm: null,
  wirksamKuerzel: null,
  wirksam: null,
  wirksamNotiz: null,
  quelle: 'manuell',
  sortOrder,
});

export const massnahmeEntwurfAus = (m: SifaGbuMassnahme): MassnahmeEntwurf => ({
  art: m.art,
  text: m.text || '',
  psaCode: m.psaCode ?? null,
  verweis: m.verweis ?? null,
  verantwortlich: m.verantwortlich ?? null,
  termin: m.termin ?? null,
  umgesetztGemeldetAm: m.umgesetztGemeldetAm ?? null,
  umgesetztGemeldetVon: m.umgesetztGemeldetVon ?? null,
  umgesetztKommentar: m.umgesetztKommentar ?? null,
  wirksamGeprueftAm: m.wirksamGeprueftAm ?? null,
  wirksamKuerzel: m.wirksamKuerzel ?? null,
  wirksam: m.wirksam ?? null,
  wirksamNotiz: m.wirksamNotiz ?? null,
  quelle: m.quelle,
  sortOrder: m.sortOrder,
});

/**
 * Umsetzung und Wirksamkeit aus `stand` in die FRISCH gelesene Zeile
 * uebernehmen — alles andere (Art, Text, PSA, Termin, Verantwortlich) bleibt
 * wie in der Datenbank. Leere Texte werden null; ein Meldedatum ohne
 * Absender gilt als „Innendienst".
 */
export const mitStandFeldern = (frisch: MassnahmeEntwurf, stand: MassnahmeEntwurf): MassnahmeEntwurf => {
  const gemeldetAm = leerZuNull(stand.umgesetztGemeldetAm);
  return {
    ...frisch,
    umgesetztGemeldetAm: gemeldetAm,
    umgesetztGemeldetVon: leerZuNull(stand.umgesetztGemeldetVon) ?? (gemeldetAm ? 'Innendienst' : null),
    umgesetztKommentar: leerZuNull(stand.umgesetztKommentar),
    wirksamGeprueftAm: leerZuNull(stand.wirksamGeprueftAm),
    wirksamKuerzel: leerZuNull(stand.wirksamKuerzel),
    wirksam: typeof stand.wirksam === 'boolean' ? stand.wirksam : null,
    wirksamNotiz: leerZuNull(stand.wirksamNotiz),
  };
};

/** PSA-Code nur bei Art P; leerer Termin wird null („laufend"), ungueltiger ebenso. */
export const bereinigeMassnahme = (e: MassnahmeEntwurf): MassnahmeEntwurf => ({
  ...e,
  text: (e.text || '').trim(),
  psaCode: e.art === 'P' ? leerZuNull(e.psaCode) : null,
  verantwortlich: leerZuNull(e.verantwortlich),
  termin: istIsoDatum(e.termin) ? (e.termin as string).trim() : null,
  // Umsetzung und Wirksamkeit wie der Dienst (leerZuNull) — sonst nie „gespeichert".
  umgesetztGemeldetAm: leerZuNull(e.umgesetztGemeldetAm),
  umgesetztGemeldetVon: leerZuNull(e.umgesetztGemeldetVon),
  umgesetztKommentar: leerZuNull(e.umgesetztKommentar),
  wirksamGeprueftAm: leerZuNull(e.wirksamGeprueftAm),
  wirksamKuerzel: leerZuNull(e.wirksamKuerzel),
  wirksam: typeof e.wirksam === 'boolean' ? e.wirksam : null,
  wirksamNotiz: leerZuNull(e.wirksamNotiz),
});

export const massnahmeGeaendert = (m: SifaGbuMassnahme, e: MassnahmeEntwurf): boolean =>
  !gleich(bereinigeMassnahme(massnahmeEntwurfAus(m)), bereinigeMassnahme(e));

/** Eine NEUE Massnahme hat Eingaben gegenueber ihrer Vorlage (Art, Verantwortlich, Termin …) — auch ohne Text. */
export const massnahmeBegonnen = (vorlage: MassnahmeEntwurf, e: MassnahmeEntwurf): boolean =>
  !gleich(bereinigeMassnahme(vorlage), bereinigeMassnahme(e));

export const pruefeMassnahme = (e: MassnahmeEntwurf): string[] => {
  const fehler: string[] = [];
  if (!(e.text || '').trim()) fehler.push('Bitte die Maßnahme beschreiben.');
  if ((e.termin || '').trim() && !istIsoDatum(e.termin)) fehler.push('Der Termin ist kein gültiges Datum.');
  return fehler;
};

/**
 * Die Massnahmen einer Gefaehrdung in der Reihenfolge, in der sie auch im
 * PDF stehen (gbuLogic.baueFassungsInhalt): sortOrder, dann Anlagezeit, dann
 * id. Keine eigene STOP-Sortierung — Editor und Blatt sollen gleich aussehen.
 */
export const massnahmenVon = (gefaehrdungId: string, massnahmen: ReadonlyArray<SifaGbuMassnahme>): SifaGbuMassnahme[] =>
  (massnahmen ?? [])
    .filter((m) => m.gefaehrdungId === gefaehrdungId)
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || (a.createdAt || '').localeCompare(b.createdAt || '') || a.id.localeCompare(b.id));

// ─── Vorsorge ───────────────────────────────────────────────────────────────

export const VORSORGE_ART_LABEL: Record<VorsorgeArt, string> = {
  pflicht: 'Pflichtvorsorge',
  angebot: 'Angebotsvorsorge',
  wunsch: 'Wunschvorsorge',
};

export const VORSORGE_ARTEN: VorsorgeArt[] = ['pflicht', 'angebot', 'wunsch'];

export const vorsorgeVon = (gefaehrdungId: string, vorsorge: ReadonlyArray<SifaGbuVorsorge>): SifaGbuVorsorge[] =>
  (vorsorge ?? []).filter((v) => v.gefaehrdungId === gefaehrdungId).sort((a, b) => (a.createdAt || '').localeCompare(b.createdAt || ''));

export const vorsorgeEntwurfAus = (v: SifaGbuVorsorge): VorsorgeEntwurf => ({
  anlassCode: v.anlassCode,
  messwert: v.messwert ?? null,
  einheit: v.einheit ?? null,
  artVorschlag: v.artVorschlag ?? null,
  art: v.art,
  begruendung: v.begruendung ?? null,
  quelle: v.quelle,
});

/**
 * Ein neuer Vorsorgeanlass. Die Art folgt dem Vorschlag (Katalogregel +
 * Messwert); ohne Vorschlag die Standardart des Anlasses aus dem Katalog.
 */
export const neueVorsorge = (eingabe: {
  anlassCode: string;
  einheit: string | null;
  standardArt: VorsorgeArt;
  artVorschlag: VorsorgeArt | null;
}): VorsorgeEntwurf => ({
  anlassCode: eingabe.anlassCode,
  messwert: null,
  einheit: eingabe.einheit,
  artVorschlag: eingabe.artVorschlag,
  art: eingabe.artVorschlag ?? eingabe.standardArt,
  begruendung: null,
  quelle: 'manuell',
});

/**
 * Nach einer Aenderung von Anlass oder Messwert: der neue Vorschlag wird
 * eingetragen. Die bestaetigte Art folgt ihm nur, solange der Nutzer sie
 * nicht bewusst anders gesetzt hat (Art == alter Vorschlag bzw. Standardart).
 */
export const vorsorgeMitVorschlag = (e: VorsorgeEntwurf, neuerVorschlag: VorsorgeArt | null, standardArt: VorsorgeArt): VorsorgeEntwurf => {
  const folgt = e.art === (e.artVorschlag ?? standardArt);
  return {
    ...e,
    artVorschlag: neuerVorschlag,
    art: folgt ? (neuerVorschlag ?? standardArt) : e.art,
  };
};

/**
 * Anlass gewechselt: ein Messwert gehoert zur Regel des ALTEN Anlasses.
 * Ohne Regel beim neuen Anlass fallen Messwert und Einheit weg; mit Regel
 * gilt deren Einheit — bei anderer Einheit faellt der Messwert weg (87 dB(A)
 * sind keine 87 m/s²).
 */
export const vorsorgeNachAnlasswechsel = (e: VorsorgeEntwurf, anlassCode: string, regel: { einheit: string } | null | undefined): VorsorgeEntwurf => {
  if (!regel) return { ...e, anlassCode, messwert: null, einheit: null };
  const gleicheEinheit = (e.einheit || '').trim() === regel.einheit.trim();
  return { ...e, anlassCode, einheit: regel.einheit, messwert: gleicheEinheit ? e.messwert : null };
};

export const bereinigeVorsorge = (e: VorsorgeEntwurf): VorsorgeEntwurf => ({
  ...e,
  anlassCode: (e.anlassCode || '').trim(),
  messwert: e.messwert != null && Number.isFinite(e.messwert) ? e.messwert : null,
  einheit: leerZuNull(e.einheit),
  begruendung: leerZuNull(e.begruendung),
});

export const vorsorgeGeaendert = (v: SifaGbuVorsorge, e: VorsorgeEntwurf): boolean =>
  !gleich(bereinigeVorsorge(vorsorgeEntwurfAus(v)), bereinigeVorsorge(e));

/** Weicht die bestaetigte Art vom gerechneten Vorschlag ab? Dann braucht es eine Begruendung (Hinweis, keine Sperre). */
export const vorsorgeAbweichung = (e: VorsorgeEntwurf): boolean =>
  e.artVorschlag != null && e.art !== e.artVorschlag;

// ─── KI-Pruefbildschirm ─────────────────────────────────────────────────────

/** Schluessel der Haken: Gefaehrdung, Massnahme und Vorsorge je Gefaehrdung, Kopf je Feld bzw. Listeneintrag. */
export const kiKey = {
  g: (g: string) => `g|${g}`,
  m: (g: string, m: string) => `m|${g}|${m}`,
  v: (g: string, v: string) => `v|${g}|${v}`,
  k: (feld: string, i?: number) => (i === undefined ? `k|${feld}` : `k|${feld}|${i}`),
};

type KopfTextFeld = 'beteiligte' | 'unterlagen' | 'psaGrundausstattung' | 'randbedingungen' | 'umwelt' | 'koordination';
const KOPF_TEXT_FELDER: Array<{ feld: KopfTextFeld; label: string }> = [
  { feld: 'beteiligte', label: 'Beteiligte' },
  { feld: 'unterlagen', label: 'Unterlagen' },
  { feld: 'psaGrundausstattung', label: 'PSA-Grundausstattung' },
  { feld: 'randbedingungen', label: 'Randbedingungen' },
  { feld: 'umwelt', label: 'Umwelt' },
  { feld: 'koordination', label: 'Koordination' },
];

/** Eine Zeile des Kopf-Vorschlags im Pruefbildschirm. `gesperrt`: Feld schon gefuellt bzw. Eintrag schon vorhanden — wird nie ueberschrieben. */
export interface KopfVorschlagZeile {
  key: string;
  label: string;
  wert: string;
  gesperrt: boolean;
}

const enthaelt = (liste: ReadonlyArray<string>, wert: string): boolean => {
  const k = wert.trim().toLocaleLowerCase('de');
  return liste.some((x) => (x || '').trim().toLocaleLowerCase('de') === k);
};

/**
 * Der Kopf-Vorschlag als Zeilen. Ein bereits GEFUELLTES Textfeld und ein
 * schon vorhandener Listeneintrag sind gesperrt: uebernehmeKiAuswahl
 * ergaenzt nur, es ueberschreibt nie (Plan §6.2) — ein Haken dort wuerde
 * etwas versprechen, das nicht passiert.
 */
export const kopfVorschlagZeilen = (kopf: GbuKiKopfVorschlag | null | undefined, ist: GbuKopfEntwurf): KopfVorschlagZeile[] => {
  const out: KopfVorschlagZeile[] = [];
  if (!kopf) return out;
  bereinigeListe(kopf.vorschriften).forEach((v, i) =>
    out.push({ key: kiKey.k('vorschriften', i), label: 'Rechtsgrundlage', wert: v, gesperrt: enthaelt(ist.vorschriften ?? [], v) }));
  for (const { feld, label } of KOPF_TEXT_FELDER) {
    const wert = (kopf[feld] || '').trim();
    if (wert) out.push({ key: kiKey.k(feld), label, wert, gesperrt: !!(ist[feld] || '').trim() });
  }
  bereinigeListe(kopf.orgMassnahmen).forEach((v, i) =>
    out.push({ key: kiKey.k('orgMassnahmen', i), label: 'Organisatorische Maßnahme', wert: v, gesperrt: enthaelt(ist.orgMassnahmen ?? [], v) }));
  for (const { feld, label } of NOTFALL_FELDER) {
    const wert = (kopf.notfall?.[feld] || '').trim();
    if (wert) out.push({ key: kiKey.k(`notfall.${feld}`), label: `Notfall: ${label}`, wert, gesperrt: !!(ist.notfall?.[feld] || '').trim() });
  }
  return out;
};

/**
 * Die Vorauswahl des Pruefbildschirms. KI-Zeilen sind vorangehakt,
 * ERSATZ-Zeilen (Katalogtreffer bei KI-Ausfall) nie — ein Ausfall darf nicht
 * wie ein fertiger Vorschlag aussehen. Gesperrte Kopf-Zeilen sind nie
 * angehakt.
 */
export const vorauswahlKi = (vorschlag: GbuKiVorschlag, ist: GbuKopfEntwurf): Set<string> => {
  const haken = new Set<string>();
  let alleKi = vorschlag.gefaehrdungen.length > 0;
  for (const g of vorschlag.gefaehrdungen) {
    if (g.quelle !== 'ki') { alleKi = false; continue; }
    haken.add(kiKey.g(g.tempId));
    g.massnahmen.forEach((m) => haken.add(kiKey.m(g.tempId, m.tempId)));
    g.vorsorge.forEach((v) => haken.add(kiKey.v(g.tempId, v.tempId)));
  }
  // Der Kopf hat keine eigene Quelle: er gilt als KI, wenn keine Ersatz-Zeile dabei ist.
  const kopfIstKi = vorschlag.gefaehrdungen.length === 0 || alleKi;
  if (kopfIstKi) {
    for (const z of kopfVorschlagZeilen(vorschlag.kopf, ist)) if (!z.gesperrt) haken.add(z.key);
  }
  return haken;
};

/** Haken an/aus; eine Gefaehrdung ohne Haken nimmt ihre Massnahmen und Vorsorge mit. */
export const schalteKiHaken = (haken: ReadonlySet<string>, key: string): Set<string> => {
  const neu = new Set(haken);
  if (neu.has(key)) neu.delete(key); else neu.add(key);
  return neu;
};

/** Alle Zeilen einer Gefaehrdung an oder aus. */
export const setzeKiGefaehrdung = (haken: ReadonlySet<string>, g: GbuKiVorschlagGefaehrdung, an: boolean): Set<string> => {
  const neu = new Set(haken);
  const keys = [kiKey.g(g.tempId), ...g.massnahmen.map((m) => kiKey.m(g.tempId, m.tempId)), ...g.vorsorge.map((v) => kiKey.v(g.tempId, v.tempId))];
  for (const k of keys) { if (an) neu.add(k); else neu.delete(k); }
  return neu;
};

/**
 * Aus den Haken die Auswahl fuer uebernehmeKiAuswahl. Massnahmen und
 * Vorsorge zaehlen nur unter einer angehakten Gefaehrdung; gesperrte
 * Kopf-Zeilen fallen heraus, auch wenn ein Haken gesetzt waere.
 */
export const baueKiAuswahl = (vorschlag: GbuKiVorschlag, haken: ReadonlySet<string>, ist: GbuKopfEntwurf): GbuKiAuswahl => {
  const gefaehrdungen = vorschlag.gefaehrdungen
    .filter((g) => haken.has(kiKey.g(g.tempId)))
    .map((g) => ({
      vorschlag: g,
      massnahmenTempIds: g.massnahmen.filter((m) => haken.has(kiKey.m(g.tempId, m.tempId))).map((m) => m.tempId),
      vorsorgeTempIds: g.vorsorge.filter((v) => haken.has(kiKey.v(g.tempId, v.tempId))).map((v) => v.tempId),
    }));

  const zeilen = kopfVorschlagZeilen(vorschlag.kopf, ist).filter((z) => !z.gesperrt && haken.has(z.key));
  const nimm = (prefix: string) => zeilen.filter((z) => z.key === prefix || z.key.startsWith(`${prefix}|`)).map((z) => z.wert);
  const kopf: GbuKiKopfVorschlag = {};
  const vorschriften = nimm('k|vorschriften');
  if (vorschriften.length) kopf.vorschriften = vorschriften;
  const org = nimm('k|orgMassnahmen');
  if (org.length) kopf.orgMassnahmen = org;
  for (const { feld } of KOPF_TEXT_FELDER) {
    const w = nimm(`k|${feld}`)[0];
    if (w) kopf[feld] = w;
  }
  const notfall: Partial<GbuNotfall> = {};
  for (const { feld } of NOTFALL_FELDER) {
    const w = nimm(`k|notfall.${feld}`)[0];
    if (w) notfall[feld] = w;
  }
  if (Object.keys(notfall).length) kopf.notfall = notfall;
  return { gefaehrdungen, kopf };
};

/** Wie viele Zeilen die Auswahl uebernimmt (Beschriftung des Knopfs). */
export const zaehleKiAuswahl = (a: GbuKiAuswahl): number => {
  let n = 0;
  for (const g of a.gefaehrdungen) n += 1 + g.massnahmenTempIds.length + g.vorsorgeTempIds.length;
  const k = a.kopf;
  n += (k.vorschriften?.length ?? 0) + (k.orgMassnahmen?.length ?? 0);
  for (const { feld } of KOPF_TEXT_FELDER) if (k[feld]) n += 1;
  n += Object.keys(k.notfall ?? {}).length;
  return n;
};

/**
 * Text, wenn der Pruefbildschirm nichts anzubieten hat. Ohne erfasste
 * Gefaehrdung ist eine leere KI-Antwort KEIN Befund — sie darf nie wie
 * „alles erledigt" aussehen (Plan §6.2: ein Ausfall sieht nie wie „keine
 * Gefaehrdung" aus).
 */
export const kiLeerText = (vorhandeneGefaehrdungen: number, ersatz: boolean): string => {
  if (ersatz) return 'Auch der Katalog hat für diesen Arbeitsbereich nichts Neues — bitte die Gefährdungen von Hand erfassen.';
  return vorhandeneGefaehrdungen === 0
    ? 'Die KI hat keine Gefährdungen geliefert — das heißt nicht, dass es keine gibt. Bitte den Ersatzvorschlag aus dem Katalog prüfen oder von Hand erfassen.'
    : 'Kein neuer Vorschlag — alles Vorgeschlagene ist schon erfasst. Das ersetzt die fachliche Prüfung nicht.';
};

/** Ein einzelner Vorschlag aus der KI-Pruefung als Vorschlag fuer den Pruefbildschirm. */
export const vorschlagAusEinzel = (g: GbuKiVorschlagGefaehrdung): GbuKiVorschlag => ({
  gefaehrdungen: [g],
  kopf: {},
  hinweise: [],
  abgeschnitten: false,
});

// ─── Feste Hinweise ─────────────────────────────────────────────────────────

export const HINWEIS_RANG: Record<GbuHinweis['stufe'], number> = { sperre: 0, warnung: 1, hinweis: 2 };

export const HINWEIS_TON: Record<GbuHinweis['stufe'], string> = {
  sperre: 'bg-red-50 border-red-200 text-red-800',
  warnung: 'bg-amber-50 border-amber-200 text-amber-900',
  hinweis: 'bg-slate-50 border-slate-200 text-slate-700',
};

export const sortiereHinweise = (liste: ReadonlyArray<GbuHinweis>): GbuHinweis[] =>
  [...(liste ?? [])].sort((a, b) => HINWEIS_RANG[a.stufe] - HINWEIS_RANG[b.stufe]);

export const sperren = (liste: ReadonlyArray<GbuHinweis>): GbuHinweis[] => (liste ?? []).filter((h) => h.stufe === 'sperre');

/** Die Hinweise, die an einer bestimmten Gefaehrdung haengen (Markierung an der Karte). */
export const hinweiseZu = (liste: ReadonlyArray<GbuHinweis>, gefaehrdungId: string): GbuHinweis[] =>
  (liste ?? []).filter((h) => h.gefaehrdungId === gefaehrdungId);

// ─── Fassungen und Freigabe ─────────────────────────────────────────────────

/** Die Fassung, die gerade zur Freigabe liegt (eingefroren, nicht freigegeben); sonst null. */
export const offeneFassung = (voll: Pick<GbuVoll, 'gbu' | 'fassungen'>): SifaGbuFassung | null => {
  if (voll.gbu.status !== 'in_freigabe') return null;
  return voll.fassungen.find((f) => f.fassung === voll.gbu.fassung && !f.freigegebenAm) ?? null;
};

/** Die gueltige (zuletzt freigegebene) Fassung; sonst null. */
export const gueltigeFassung = (voll: Pick<GbuVoll, 'gbu' | 'fassungen'>): SifaGbuFassung | null => {
  const nr = voll.gbu.freigegebeneFassung;
  if (nr == null) return null;
  return voll.fassungen.find((f) => f.fassung === nr && !!f.freigegebenAm) ?? null;
};

/**
 * Freigegeben, aber ohne abgelegtes PDF (Ablage scheiterte, oder die
 * Freigabe kam ueber den Link): diese Fassung braucht beim Oeffnen ihr PDF.
 * Nie in der Nur-Ansicht — die Ablage schreibt.
 */
export const pdfNachholen = (voll: Pick<GbuVoll, 'gbu' | 'fassungen'>, isReadOnly: boolean): SifaGbuFassung | null => {
  if (isReadOnly) return null;
  const f = gueltigeFassung(voll);
  return f && !f.pdfDokumentId ? f : null;
};

export interface GbuKnoepfe {
  /** Kopf, Gefaehrdungen, Massnahmen bearbeiten. */
  bearbeiten: boolean;
  zurFreigabe: boolean;
  /** Freigabedialog einer eingefrorenen Fassung (Weg waehlen). */
  freigabeFortsetzen: boolean;
  zurueckziehen: boolean;
  fortschreiben: boolean;
  archivieren: boolean;
  loeschen: boolean;
  zeitBuchen: boolean;
  ki: boolean;
}

/**
 * Welche Handgriffe der Editor anbietet. Bearbeitet wird nur ein Entwurf
 * (gbuLogic.istBearbeitbar); eine freigegebene GBU wird fortgeschrieben,
 * nicht geaendert. Geloescht wird nur, was nie eingefroren wurde (Fassungen
 * sind per Fremdschluessel geschuetzt). Nur-Ansicht sperrt alles Schreibende,
 * auch Zeit buchen und Freigabe.
 */
export const gbuKnoepfe = (g: Pick<SifaGbu, 'status'>, anzahlFassungen: number, isReadOnly: boolean, sperrenAnzahl: number): GbuKnoepfe => {
  const ro = isReadOnly;
  return {
    bearbeiten: !ro && g.status === 'entwurf',
    zurFreigabe: !ro && g.status === 'entwurf' && sperrenAnzahl === 0,
    freigabeFortsetzen: !ro && g.status === 'in_freigabe',
    zurueckziehen: !ro && g.status === 'in_freigabe',
    fortschreiben: !ro && g.status === 'freigegeben',
    archivieren: !ro && (g.status === 'freigegeben' || (g.status === 'entwurf' && anzahlFassungen > 0)),
    loeschen: !ro && g.status === 'entwurf' && anzahlFassungen === 0,
    zeitBuchen: !ro && g.status !== 'archiviert',
    ki: !ro && g.status === 'entwurf',
  };
};

/** Warum „Zur Freigabe" gesperrt ist — fuer Tooltip und Zeile darunter; null = frei. */
export const zurFreigabeGrund = (g: Pick<SifaGbu, 'status'>, sperrenAnzahl: number, isReadOnly: boolean): string | null => {
  if (isReadOnly) return 'Nur-Ansicht: keine Freigabe.';
  if (g.status !== 'entwurf') return null;
  if (sperrenAnzahl > 0) return sperrenAnzahl === 1 ? 'Ein Punkt verhindert die Freigabe (siehe Prüfhinweise).' : `${sperrenAnzahl} Punkte verhindern die Freigabe (siehe Prüfhinweise).`;
  return null;
};

/** Pruefdatum im Freigabedialog: gueltiges Datum, nicht vor heute. */
export const pruefePruefdatum = (iso: string | null | undefined, heute: string): string | null => {
  if (!istIsoDatum(iso)) return 'Bitte ein gültiges Prüfdatum wählen.';
  if ((iso as string) < heute) return 'Das Prüfdatum liegt in der Vergangenheit.';
  return null;
};

export const ROLLE_LABEL: Record<UnterschriftRolle, string> = {
  erstellt: 'Erstellt',
  geprueft: 'Geprüft',
  freigegeben: 'Freigegeben',
};

/** Die Rollen, die zur GBU gehoeren: „Geprueft" nur, wenn eingetragen. */
export const rollenDerGbu = (rollen: GbuRollen | null | undefined): UnterschriftRolle[] =>
  rollen?.geprueft ? ['erstellt', 'geprueft', 'freigegeben'] : ['erstellt', 'freigegeben'];

/** Vorgabe von Name und Funktion je Rolle aus dem Kopf. */
export const rolleVorgabe = (rollen: GbuRollen | null | undefined, rolle: UnterschriftRolle): { name: string; funktion: string } => {
  const r = rolle === 'geprueft' ? rollen?.geprueft : rollen?.[rolle];
  return { name: (r?.name || '').trim(), funktion: (r?.funktion || '').trim() };
};

/** Fuegt eine Unterschrift ein; eine vorhandene derselben Rolle wird ersetzt. */
export const setzeUnterschrift = (liste: ReadonlyArray<GbuUnterschrift>, u: GbuUnterschrift): GbuUnterschrift[] =>
  [...(liste ?? []).filter((x) => x.rolle !== u.rolle), u].sort((a, b) => rollenRang(a.rolle) - rollenRang(b.rolle));

const rollenRang = (r: UnterschriftRolle): number => (r === 'erstellt' ? 0 : r === 'geprueft' ? 1 : 2);

/**
 * Digital abschliessen braucht die Unterschrift der Rolle „Freigegeben"
 * (Unternehmer bzw. verantwortliche Fuehrungskraft, Plan §8). Ohne sie ist
 * nichts freigegeben; dann bleibt der Freigabe-Link.
 */
export const fehlendeDigitaleUnterschrift = (liste: ReadonlyArray<GbuUnterschrift>): string | null => {
  const frei = (liste ?? []).find((u) => u.rolle === 'freigegeben');
  if (!frei) return 'Es fehlt die Unterschrift „Freigegeben" (Unternehmer) — am Gerät unterschreiben oder den Freigabe-Link schicken.';
  if (!(frei.name || '').trim()) return 'Die Unterschrift „Freigegeben" braucht einen Namen.';
  return null;
};

/** Die Unterschriften, die schon an der Fassung haengen (z. B. per Link), zuerst; dann die am Geraet gesammelten. */
export const vereinigeUnterschriften = (an: ReadonlyArray<GbuUnterschrift>, neu: ReadonlyArray<GbuUnterschrift>): GbuUnterschrift[] => {
  let out = [...(an ?? [])];
  for (const u of neu ?? []) out = setzeUnterschrift(out, u);
  return out;
};

/**
 * Haengt eine Unterschrift am Geraet SOFORT an die Fassung (unterschreibeFassung)?
 * Ja fuer „Erstellt" und „Geprueft" — sie geben nichts frei und muessen auch
 * dann erhalten bleiben, wenn der Unternehmer spaeter per Link freigibt.
 * „Freigegeben" geht erst mit `freigeben` hinaus: diese Unterschrift IST die
 * Freigabe.
 */
export const sofortAnhaengen = (rolle: UnterschriftRolle): boolean => rolle !== 'freigegeben';

/** Rollen der GBU, die an der Fassung noch keine Unterschrift haben. */
export const offeneRollen = (rollen: ReadonlyArray<UnterschriftRolle>, vorhanden: ReadonlyArray<GbuUnterschrift>): UnterschriftRolle[] =>
  (rollen ?? []).filter((r) => !(vorhanden ?? []).some((u) => u.rolle === r));

/** Eine Unterschrift am Geraet des Innendienstes; leerer Name faellt auf die Vorgabe aus den Rollen zurueck. */
export const geraeteUnterschrift = (eingabe: {
  rolle: UnterschriftRolle;
  name: string;
  vorgabe: { name: string; funktion: string };
  bild: string;
  jetzt: string;
}): GbuUnterschrift => ({
  rolle: eingabe.rolle,
  name: (eingabe.name || '').trim() || eingabe.vorgabe.name,
  funktion: eingabe.vorgabe.funktion || null,
  datum: eingabe.jetzt,
  bild: eingabe.bild,
  weg: 'geraet',
});

/**
 * Der Freigabe-Link. `pathname` bleibt drin, weil die App nicht zwingend an
 * der Wurzel liegt; der Hash-Router ist derselbe wie bei
 * `#/unterweisung/<token>` (unterweisungModulLogic.linkUrl).
 */
export const freigabeLinkUrl = (origin: string, pathname: string, token: string): string =>
  `${origin}${pathname || '/'}#/gbu-freigabe/${encodeURIComponent(token)}`;

/** Leere Unterschriftsfelder im PDF: nur auf dem Papierweg und solange nichts digital unterschrieben ist. */
export const pdfOptionenFuer = (stand: 'entwurf' | 'freigabe_papier' | 'freigabe_digital' | 'freigegeben', f: Pick<SifaGbuFassung, 'unterschriften' | 'freigabeWeg'> | null): {
  unterschriften: GbuUnterschrift[];
  leereUnterschriftsfelder: boolean;
  entwurf: boolean;
} => {
  const unterschriften = [...(f?.unterschriften ?? [])];
  switch (stand) {
    case 'entwurf': return { unterschriften: [], leereUnterschriftsfelder: true, entwurf: true };
    case 'freigabe_papier': return { unterschriften, leereUnterschriftsfelder: true, entwurf: false };
    case 'freigabe_digital': return { unterschriften, leereUnterschriftsfelder: true, entwurf: false };
    default: return { unterschriften, leereUnterschriftsfelder: f?.freigabeWeg === 'papier' && unterschriften.length === 0, entwurf: false };
  }
};

/** Inhalt und Freigabedaten zusammen — fuer die Anzeige „Fassung n vom …". */
export const fassungText = (f: Pick<SifaGbuFassung, 'fassung' | 'freigegebenAm' | 'freigabeWeg'>): string => {
  if (!f.freigegebenAm) return `Fassung ${f.fassung} · in Freigabe`;
  const weg = f.freigabeWeg === 'papier' ? 'Papier' : f.freigabeWeg === 'digital' ? 'digital' : '';
  return `Fassung ${f.fassung} · freigegeben am ${formatTag(f.freigegebenAm)}${weg ? ` (${weg})` : ''}`;
};

/** Fingerabdruck eines Editor-Stands: Kopf-Revision und Status, dazu Id + Aenderungszeit jeder Zeile. */
const fingerabdruck = (v: Pick<GbuVoll, 'gbu' | 'gefaehrdungen' | 'massnahmen' | 'vorsorge'>): string =>
  JSON.stringify([
    v.gbu.id, v.gbu.revision, v.gbu.status, v.gbu.fassung,
    ...[v.gefaehrdungen, v.massnahmen, v.vorsorge].map((liste) =>
      [...(liste ?? [])].map((z) => `${z.id}@${z.updatedAt}`).sort()),
  ]);

/**
 * Hat sich der Stand zwischen Anzeige und Einfrieren geaendert (zweiter
 * Bearbeiter, KI-Uebernahme in einem anderen Fenster)? Dann wird NICHT
 * eingefroren: der Nutzer soll sehen, was er freigibt.
 */
export const standGeaendert = (
  alt: Pick<GbuVoll, 'gbu' | 'gefaehrdungen' | 'massnahmen' | 'vorsorge'>,
  neu: Pick<GbuVoll, 'gbu' | 'gefaehrdungen' | 'massnahmen' | 'vorsorge'>,
): boolean => fingerabdruck(alt) !== fingerabdruck(neu);

// ─── Zeit buchen ────────────────────────────────────────────────────────────

/** Eigener Journalpfad (Plan §10.5) — nicht 'einweisung' wie die Altpfade. */
export const GBU_JOURNAL_SOURCE = 'gbu' as const;

/** Vorgabetext fuer die Taetigkeit: Erstellung bzw. Fortschreibung. */
export const zeitBeschreibungVorgabe = (g: Pick<SifaGbu, 'nummer' | 'titel' | 'fassung'>): string =>
  `${(g.fassung || 1) > 1 ? 'Fortschreibung' : 'Erstellung'} Gefährdungsbeurteilung Nr. ${g.nummer} – ${(g.titel || '').trim() || 'ohne Titel'}`;

/**
 * Ohne buchbare Stundenposition landet die Zeit NUR im Taetigkeitsnachweis —
 * das muss im Zeit-Dialog stehen, nie still passieren (Praxistest: „Zeit
 * buchen muss in Leistung & Budget landen").
 */
export const KEINE_STUNDENPOSITION_TEXT =
  'Im LV gibt es keine Stundenposition — die Zeit erscheint nur im Tätigkeitsnachweis, nicht in Leistung & Budget. '
  + 'Bitte in Leistung & Budget eine Position anlegen (z. B. „Gefährdungsbeurteilung nach Zeitaufwand, Std.") und danach in „Erfasste Zeiten" abrechnen.';

/**
 * Vorauswahl im Abrechnungsdialog: die erste zur GBU passende Position
 * (gbuBooking.istGbuPassend) — sonst keine Vorgabe, dann entscheidet der
 * Dialog wie gewohnt. „Ohne Leistungsbuchung" ist nie vorausgewaehlt.
 */
export const vorauswahlIds = <T extends { id: string }>(positionen: ReadonlyArray<T>, passend: (p: T) => boolean): string[] => {
  const erste = (positionen ?? []).find(passend);
  return erste ? [erste.id] : [];
};

/** Minuten auf Viertelstunden gerundet (kaufmaennisch), mindestens eine Viertelstunde, als Stunden. */
export const viertelstunden = (minuten: number | null | undefined): number => {
  const m = Number(minuten);
  if (!Number.isFinite(m) || m <= 0) return 0;
  return Math.max(0.25, Math.round((m / 60) * 4) / 4);
};

/** Das, was die Liste „Erfasste Zeiten" von einer Taetigkeit braucht (gbuService.ladeGbuZeiten). */
export interface ZeitZeile {
  id: string;
  datum: string;
  dauerMinuten: number;
  gebuchtAm: string | null;
}

/**
 * Darf eine erfasste Zeit nachtraeglich abgerechnet werden? Nur, solange sie
 * nicht gebucht ist, nicht in der Nur-Ansicht und nur, wenn es eine passende
 * LV-Position und einen Schreibweg ins Projekt gibt. Der harte Riegel bleibt
 * die RPC — das hier entscheidet nur, ob der Knopf da ist.
 */
export const kannNachbuchen = (z: Pick<ZeitZeile, 'gebuchtAm'>, isReadOnly: boolean, kannAbrechnen: boolean): boolean =>
  !isReadOnly && kannAbrechnen && !z.gebuchtAm;

export type GbuZeitStand = 'offen' | 'gebucht' | 'gutschrift_fehlt';

/**
 * Abgleich Riegel ↔ Projekt fuer eine erfasste Zeit.
 *
 *   offen            — kein Riegel (`gebuchtAm` leer): abrechnen moeglich.
 *   gebucht          — Riegel gesetzt UND das serviceLog traegt den Eintrag
 *                      `gbu-zeit-<id>` (gbuBooking.gbuZeitLogId).
 *   gutschrift_fehlt — Riegel gesetzt mit Position und Menge, aber kein
 *                      solcher Eintrag: die Antwort der RPC ging verloren
 *                      oder die Gutschrift scheiterte danach. Nachholen
 *                      OHNE erneuten Riegel.
 *
 * Altdaten ohne Position oder Menge gelten als `gebucht` — dort laesst sich
 * nichts sicher nachholen, und ein Angebot fuehrte zu geratenen Mengen.
 *
 * ACHTUNG fuer kuenftige Abrechnungswege: JEDE Abrechnung einer Zeile aus
 * `sifa_taetigkeiten` (z. B. der Taetigkeitsnachweis, Stufe 3) muss ihren
 * Log-Eintrag mit derselben Id `gbu-zeit-<taetigkeitId>`
 * (gbuBooking.gbuZeitLogId) schreiben. Sonst steht der Riegel, der Abgleich
 * hier findet den Eintrag nicht — und zeigt zu Unrecht „Gutschrift fehlt"
 * samt Nachholen-Knopf, also eine Doppelbuchung auf Knopfdruck.
 */
export const gbuZeitStand = (
  zeit: Pick<GbuZeit, 'id' | 'gebuchtAm' | 'gebuchtePositionId' | 'gebuchteMenge'>,
  serviceLog: ReadonlyArray<{ id?: string | null }> | null | undefined,
): GbuZeitStand => {
  if (!zeit.gebuchtAm) return 'offen';
  const kennung = gbuZeitLogId(zeit.id);
  if ((serviceLog ?? []).some((e) => e?.id === kennung)) return 'gebucht';
  const menge = Number(zeit.gebuchteMenge);
  if ((zeit.gebuchtePositionId || '').trim() && zeit.gebuchteMenge != null && Number.isFinite(menge) && menge > 0) return 'gutschrift_fehlt';
  return 'gebucht';
};

/**
 * Was „Gutschrift nachholen" nach der Rueckfrage beim Server tut
 * (gbuService.pruefeGutschriftAufServer mit `gbu-zeit-<id>`). Das lokale
 * serviceLog kann veraltet sein — ein anderes Fenster hat vielleicht schon
 * nachgeholt und gespeichert. Gutgeschrieben wird deshalb NUR bei „fehlt";
 * „unbekannt" (Netz, Rechte) ist kein Freibrief.
 */
export const nachholEntscheid = (serverStand: 'vorhanden' | 'fehlt' | 'unbekannt'): { gutschreiben: true; meldung: null } | { gutschreiben: false; meldung: string } => {
  if (serverStand === 'fehlt') return { gutschreiben: true, meldung: null };
  if (serverStand === 'vorhanden') return { gutschreiben: false, meldung: 'Die Gutschrift ist auf dem Server schon gespeichert — bitte die Seite neu laden.' };
  return { gutschreiben: false, meldung: 'Stand konnte nicht geprüft werden — bitte später erneut versuchen.' };
};

/** Steht die Gutschrift dieser Taetigkeit schon im serviceLog (zweiter Klick, zweites Fenster)? */
export const istGutgeschrieben = (taetigkeitId: string, serviceLog: ReadonlyArray<{ id?: string | null }> | null | undefined): boolean => {
  const kennung = gbuZeitLogId(taetigkeitId);
  return (serviceLog ?? []).some((e) => e?.id === kennung);
};

export const zeitenSumme = (zeiten: ReadonlyArray<ZeitZeile>): { minuten: number; offenMinuten: number; offen: number } => {
  let minuten = 0;
  let offenMinuten = 0;
  let offen = 0;
  for (const z of zeiten ?? []) {
    const m = Number.isFinite(z.dauerMinuten) && z.dauerMinuten > 0 ? z.dauerMinuten : 0;
    minuten += m;
    if (!z.gebuchtAm) { offen += 1; offenMinuten += m; }
  }
  return { minuten, offenMinuten, offen };
};

/**
 * Satz oben im Abrechnungsdialog. „Vorbelegt" nur, wenn es eine
 * Stundenposition gibt — BillingSelectionModal belegt nur dort vor
 * (`vorgabeMenge`); bei Stueckpositionen waere der Satz falsch.
 */
export const vorabHinweisText = (eingabe: {
  dauerText: string;
  datumText: string;
  stunden: number;
  stundenpositionen: 'alle' | 'einige' | 'keine';
}): string => {
  const erfasst = `Erfasst: ${eingabe.dauerText} am ${eingabe.datumText}`;
  const std = `${eingabe.stunden.toLocaleString('de-DE')} Std. (Viertelstunden)`;
  if (eingabe.stundenpositionen === 'alle') return `${erfasst} — vorbelegt mit ${std}, bei Bedarf anpassen.`;
  if (eingabe.stundenpositionen === 'einige') return `${erfasst} — bei einer Stundenposition vorbelegt mit ${std}, bei Bedarf anpassen.`;
  return `${erfasst}.`;
};

export const pruefeZeitEingabe = (e: { datum: string; minuten: number | null; beschreibung: string }, heute: string): string[] => {
  const fehler: string[] = [];
  if (!istIsoDatum(e.datum)) fehler.push('Bitte ein gültiges Datum wählen.');
  else if (e.datum > heute) fehler.push('Das Datum liegt in der Zukunft.');
  if (e.minuten == null || !(e.minuten > 0)) fehler.push('Dauer bitte als Minuten, 1:30 oder 1,5 h eingeben.');
  else if (e.minuten > 24 * 60) fehler.push('Mehr als 24 Stunden an einem Tag — bitte auf mehrere Tage verteilen.');
  if (!(e.beschreibung || '').trim()) fehler.push('Bitte kurz beschreiben, was getan wurde.');
  return fehler;
};

// ─── Massnahmenliste ────────────────────────────────────────────────────────

export type MassnahmenFilter = 'offen' | 'ueberfaellig' | 'gemeldet' | 'erledigt' | 'alle';

export const MASSNAHMEN_FILTER_LABEL: Record<MassnahmenFilter, string> = {
  offen: 'Offen',
  ueberfaellig: 'Überfällig',
  gemeldet: 'Umgesetzt gemeldet',
  erledigt: 'Wirksam / unwirksam',
  alle: 'Alle',
};

export const MASSNAHMEN_FILTER: MassnahmenFilter[] = ['offen', 'ueberfaellig', 'gemeldet', 'erledigt', 'alle'];

export const STAND_LABEL: Record<MassnahmeListenZeile['stand'], string> = {
  offen: 'offen',
  gemeldet: 'umgesetzt gemeldet',
  wirksam: 'wirksam',
  unwirksam: 'unwirksam',
};

export const STAND_TON: Record<MassnahmeListenZeile['stand'], string> = {
  offen: 'text-slate-700 bg-slate-100 border-slate-200',
  gemeldet: 'text-blue-700 bg-blue-50 border-blue-200',
  wirksam: 'text-emerald-700 bg-emerald-50 border-emerald-200',
  unwirksam: 'text-red-700 bg-red-50 border-red-200',
};

/**
 * Welche GBUs in die Auswertung gehen: die mindestens einmal freigegebenen
 * (Plan §4.5 — Auswertungen kommen aus freigegebenen GBUs), auf Wunsch auch
 * Entwuerfe. Archivierte nie.
 */
export const gbusFuerAuswertung = (liste: ReadonlyArray<SifaGbu>, mitEntwuerfen: boolean): SifaGbu[] =>
  (liste ?? []).filter((g) => g.status !== 'archiviert' && (mitEntwuerfen || g.freigegebeneFassung != null || g.status === 'freigegeben'));

export const filtereMassnahmen = (zeilen: ReadonlyArray<MassnahmeListenZeile>, filter: MassnahmenFilter): MassnahmeListenZeile[] => {
  const l = zeilen ?? [];
  switch (filter) {
    case 'offen': return l.filter((z) => z.stand === 'offen' || z.stand === 'unwirksam');
    case 'ueberfaellig': return l.filter((z) => z.ueberfaellig);
    case 'gemeldet': return l.filter((z) => z.stand === 'gemeldet');
    case 'erledigt': return l.filter((z) => z.stand === 'wirksam' || z.stand === 'unwirksam');
    default: return [...l];
  }
};

/** Ueberfaellige zuerst, dann nach Termin (ohne Termin = laufend, ans Ende), dann GBU-Nummer. */
export const sortiereMassnahmen = (zeilen: ReadonlyArray<MassnahmeListenZeile>): MassnahmeListenZeile[] =>
  [...(zeilen ?? [])].sort((a, b) => {
    if (a.ueberfaellig !== b.ueberfaellig) return a.ueberfaellig ? -1 : 1;
    const ta = a.termin || '9999-12-31';
    const tb = b.termin || '9999-12-31';
    return ta.localeCompare(tb) || a.gbuNummer - b.gbuNummer || a.art.localeCompare(b.art);
  });

export const zaehleMassnahmen = (zeilen: ReadonlyArray<MassnahmeListenZeile>): Record<MassnahmenFilter, number> => {
  const z: Record<MassnahmenFilter, number> = { offen: 0, ueberfaellig: 0, gemeldet: 0, erledigt: 0, alle: 0 };
  for (const m of zeilen ?? []) {
    z.alle += 1;
    if (m.stand === 'offen' || m.stand === 'unwirksam') z.offen += 1;
    if (m.ueberfaellig) z.ueberfaellig += 1;
    if (m.stand === 'gemeldet') z.gemeldet += 1;
    if (m.stand === 'wirksam' || m.stand === 'unwirksam') z.erledigt += 1;
  }
  return z;
};

// ─── Betrieb (Kataster) ─────────────────────────────────────────────────────

export const GESTELLT_VON_LABEL: Record<ArbeitsmittelGestelltVon, string> = {
  eigen: 'Eigenes Arbeitsmittel',
  auftraggeber: 'Gestellt vom Auftraggeber',
  verleiher: 'Gestellt vom Verleiher',
};

export const GESTELLT_VON: ArbeitsmittelGestelltVon[] = ['eigen', 'auftraggeber', 'verleiher'];

export const STOFF_ART_LABEL: Record<StoffArt, string> = { gefahrstoff: 'Gefahrstoff', biostoff: 'Biostoff' };

export const SIGNALWORT_LABEL: Record<Signalwort, string> = { gefahr: 'Gefahr', achtung: 'Achtung' };

export const GHS_CODES = ['GHS01', 'GHS02', 'GHS03', 'GHS04', 'GHS05', 'GHS06', 'GHS07', 'GHS08', 'GHS09'] as const;

export const GHS_LABEL: Record<(typeof GHS_CODES)[number], string> = {
  GHS01: 'Explodierende Bombe',
  GHS02: 'Flamme',
  GHS03: 'Flamme über Kreis',
  GHS04: 'Gasflasche',
  GHS05: 'Ätzwirkung',
  GHS06: 'Totenkopf',
  GHS07: 'Ausrufezeichen',
  GHS08: 'Gesundheitsgefahr',
  GHS09: 'Umwelt',
};

export const leererBereich = (): ArbeitsbereichEntwurf => ({ bezeichnung: '', bereich: null, beschreibung: null, ort: null, aktiv: true });

export const bereichEntwurfAus = (b: SifaArbeitsbereich): ArbeitsbereichEntwurf => ({
  bezeichnung: b.bezeichnung || '',
  bereich: b.bereich ?? null,
  beschreibung: b.beschreibung ?? null,
  ort: b.ort ?? null,
  aktiv: b.aktiv,
});

export const bereinigeBereich = (e: ArbeitsbereichEntwurf): ArbeitsbereichEntwurf => ({
  bezeichnung: (e.bezeichnung || '').trim(),
  bereich: leerZuNull(e.bereich),
  beschreibung: leerZuNull(e.beschreibung),
  ort: leerZuNull(e.ort),
  aktiv: e.aktiv !== false,
});

export const pruefeBereich = (e: ArbeitsbereichEntwurf): string[] =>
  (e.bezeichnung || '').trim() ? [] : ['Bitte eine Bezeichnung eingeben (z. B. „Einschalen").'];

export const leeresArbeitsmittel = (): ArbeitsmittelEntwurf => ({
  bezeichnung: '', typ: null, hersteller: null, gestelltVon: 'eigen', pruefung: null, pruefIntervallMonate: null, pruefer: null, notiz: null, aktiv: true,
});

export const arbeitsmittelEntwurfAus = (a: SifaArbeitsmittel): ArbeitsmittelEntwurf => ({
  bezeichnung: a.bezeichnung || '',
  typ: a.typ ?? null,
  hersteller: a.hersteller ?? null,
  gestelltVon: a.gestelltVon,
  pruefung: a.pruefung ?? null,
  pruefIntervallMonate: a.pruefIntervallMonate ?? null,
  pruefer: a.pruefer ?? null,
  notiz: a.notiz ?? null,
  aktiv: a.aktiv,
});

export const bereinigeArbeitsmittel = (e: ArbeitsmittelEntwurf): ArbeitsmittelEntwurf => ({
  bezeichnung: (e.bezeichnung || '').trim(),
  typ: leerZuNull(e.typ),
  hersteller: leerZuNull(e.hersteller),
  gestelltVon: e.gestelltVon,
  pruefung: leerZuNull(e.pruefung),
  pruefIntervallMonate: e.pruefIntervallMonate != null && Number.isFinite(e.pruefIntervallMonate) ? Math.round(e.pruefIntervallMonate) : null,
  pruefer: leerZuNull(e.pruefer),
  notiz: leerZuNull(e.notiz),
  aktiv: e.aktiv !== false,
});

export const pruefeArbeitsmittel = (e: ArbeitsmittelEntwurf): string[] => {
  const fehler: string[] = [];
  if (!(e.bezeichnung || '').trim()) fehler.push('Bitte eine Bezeichnung eingeben.');
  const n = e.pruefIntervallMonate;
  if (n != null && (!Number.isFinite(n) || n < 1 || n > 120)) fehler.push('Das Prüfintervall liegt zwischen 1 und 120 Monaten.');
  return fehler;
};

/** Hinweis zum Pruefnachweis je Herkunft (Plan §4.2): beim AG keine eigene Frist. */
export const pruefHinweis = (a: Pick<SifaArbeitsmittel, 'gestelltVon' | 'pruefIntervallMonate' | 'pruefung'>): string | null => {
  if (a.gestelltVon === 'auftraggeber') return 'Prüfnachweis beim Auftraggeber';
  if (a.gestelltVon === 'verleiher') return 'Prüfnachweis beim Verleiher';
  if (a.pruefIntervallMonate) return `Prüfung alle ${a.pruefIntervallMonate} Monate${a.pruefung ? ` (${a.pruefung})` : ''}`;
  return a.pruefung ? `Prüfung: ${a.pruefung}` : null;
};

export const leererStoff = (art: StoffArt = 'gefahrstoff'): StoffEntwurf => ({
  art, bezeichnung: '', hersteller: null, cas: null, signalwort: null, hSaetze: [], ghs: [], risikogruppe: null,
  mengeJahr: null, sdbDatum: null, substitutionGeprueftAm: null, substitutionErgebnis: null, notiz: null, aktiv: true,
});

export const stoffEntwurfAus = (s: SifaStoff): StoffEntwurf => ({
  art: s.art,
  bezeichnung: s.bezeichnung || '',
  hersteller: s.hersteller ?? null,
  cas: s.cas ?? null,
  signalwort: s.signalwort ?? null,
  hSaetze: [...(s.hSaetze ?? [])],
  ghs: [...(s.ghs ?? [])],
  risikogruppe: s.risikogruppe ?? null,
  mengeJahr: s.mengeJahr ?? null,
  sdbDatum: s.sdbDatum ?? null,
  substitutionGeprueftAm: s.substitutionGeprueftAm ?? null,
  substitutionErgebnis: s.substitutionErgebnis ?? null,
  notiz: s.notiz ?? null,
  aktiv: s.aktiv,
});

/**
 * H-Saetze (und EUH-Saetze) aus einer Eingabe wie „H315, h318 H372 EUH208":
 * Grossschreibung, Doppelte und Unlesbares fallen weg. Kombinationen wie
 * „H300+H310" bleiben als ein Eintrag.
 */
export const parseHSaetze = (text: string | null | undefined): string[] => {
  const roh = (text ?? '').toUpperCase().split(/[\s,;]+/).map((t) => t.trim()).filter(Boolean);
  return bereinigeListe(roh.filter((t) => /^(EU)?H\d{3}[A-Z]{0,2}(\+(EU)?H\d{3}[A-Z]{0,2})*$/.test(t)));
};

/** Unlesbare Teile der H-Satz-Eingabe — fuer die Zeile unter dem Feld. */
export const unlesbareHSaetze = (text: string | null | undefined): string[] =>
  (text ?? '').toUpperCase().split(/[\s,;]+/).map((t) => t.trim()).filter(Boolean)
    .filter((t) => !/^(EU)?H\d{3}[A-Z]{0,2}(\+(EU)?H\d{3}[A-Z]{0,2})*$/.test(t));

export const bereinigeStoff = (e: StoffEntwurf): StoffEntwurf => ({
  art: e.art,
  bezeichnung: (e.bezeichnung || '').trim(),
  hersteller: leerZuNull(e.hersteller),
  cas: leerZuNull(e.cas),
  signalwort: e.art === 'gefahrstoff' ? (e.signalwort ?? null) : null,
  hSaetze: e.art === 'gefahrstoff' ? bereinigeListe((e.hSaetze ?? []).map((h) => h.toUpperCase())) : [],
  ghs: e.art === 'gefahrstoff' ? GHS_CODES.filter((c) => (e.ghs ?? []).includes(c)) : [],
  risikogruppe: e.art === 'biostoff' && e.risikogruppe != null ? klemmeRisikogruppe(e.risikogruppe) : null,
  mengeJahr: leerZuNull(e.mengeJahr),
  sdbDatum: istIsoDatum(e.sdbDatum) ? e.sdbDatum : null,
  substitutionGeprueftAm: istIsoDatum(e.substitutionGeprueftAm) ? e.substitutionGeprueftAm : null,
  substitutionErgebnis: leerZuNull(e.substitutionErgebnis),
  notiz: leerZuNull(e.notiz),
  aktiv: e.aktiv !== false,
});

const klemmeRisikogruppe = (n: number): number => Math.min(4, Math.max(1, Math.round(n)));

export const pruefeStoff = (e: StoffEntwurf): string[] => {
  const fehler: string[] = [];
  if (!(e.bezeichnung || '').trim()) fehler.push('Bitte eine Bezeichnung eingeben.');
  if (e.art === 'biostoff' && e.risikogruppe != null && (e.risikogruppe < 1 || e.risikogruppe > 4)) fehler.push('Die Risikogruppe liegt zwischen 1 und 4.');
  if ((e.sdbDatum || '').trim() && !istIsoDatum(e.sdbDatum)) fehler.push('Das Datum des Sicherheitsdatenblatts ist ungültig.');
  return fehler;
};

/** Ids der Personen, die einem Bereich laufend (bis = null) zugeordnet sind. */
export const laufendeMitarbeiterIds = (zuordnungen: ReadonlyArray<SifaMitarbeiterBereich>, bereichId: string): string[] =>
  Array.from(new Set((zuordnungen ?? []).filter((z) => z.bereichId === bereichId && !z.bis).map((z) => z.mitarbeiterId)));

export const mitarbeiterJeBereich = (zuordnungen: ReadonlyArray<SifaMitarbeiterBereich>): Record<string, number> => {
  const out: Record<string, Set<string>> = {};
  for (const z of zuordnungen ?? []) {
    if (z.bis) continue;
    (out[z.bereichId] ||= new Set()).add(z.mitarbeiterId);
  }
  return Object.fromEntries(Object.entries(out).map(([k, v]) => [k, v.size]));
};

/**
 * Wer in der Mitarbeiterauswahl steht: aktive, nicht zusammengefuehrte
 * Personen — und alle, die schon zugeordnet sind (auch deaktivierte, sonst
 * fiele ihre Zuordnung beim Speichern still weg). Nachname, Vorname.
 */
export const waehlbareMitarbeiter = (liste: ReadonlyArray<SifaMitarbeiter>, zugeordnet: ReadonlyArray<string>): SifaMitarbeiter[] =>
  [...(liste ?? [])]
    .filter((m) => !m.zusammengefuehrtIn && (m.aktiv !== false || zugeordnet.includes(m.id)))
    .sort((a, b) => (a.nachname || '').localeCompare(b.nachname || '', 'de') || (a.vorname || '').localeCompare(b.vorname || '', 'de'));

/** Suche in der Mitarbeiterauswahl: Wortteile in Name, Abteilung, Personalnummer. */
export const sucheMitarbeiter = (liste: ReadonlyArray<SifaMitarbeiter>, suche: string): SifaMitarbeiter[] => {
  const s = (suche || '').trim().toLowerCase();
  if (!s) return [...(liste ?? [])];
  const woerter = s.split(/\s+/);
  return (liste ?? []).filter((m) => {
    const text = `${m.vorname} ${m.nachname} ${m.abteilung || ''} ${m.personalnummer || ''}`.toLowerCase();
    return woerter.every((w) => text.includes(w));
  });
};

/** Warum ein Bereich nicht geloescht werden kann (eine GBU haengt daran, Fremdschluessel restrict); null = frei. */
export const bereichLoeschSperre = (bereichId: string, gbus: ReadonlyArray<Pick<SifaGbu, 'bereichId' | 'nummer'>>): string | null => {
  const treffer = (gbus ?? []).filter((g) => g.bereichId === bereichId);
  if (treffer.length === 0) return null;
  const nummern = treffer.map((g) => `Nr. ${g.nummer}`).join(', ');
  return `An diesem Bereich hängt die Gefährdungsbeurteilung ${nummern} — statt zu löschen auf „inaktiv" setzen.`;
};

export const sortiereKataster = <T extends { aktiv: boolean; sortOrder: number; bezeichnung: string }>(liste: ReadonlyArray<T>): T[] =>
  [...(liste ?? [])].sort((a, b) => Number(b.aktiv) - Number(a.aktiv) || (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.bezeichnung.localeCompare(b.bezeichnung, 'de'));

/** Leeres Kataster — Zustand vor dem ersten Laden. */
export const LEERES_KATASTER: Kataster = { bereiche: [], arbeitsmittel: [], stoffe: [], zuordnungen: [], schemaFehlt: false };
